/* GoEye lens selector.
   Moved out of sections/lens-selector.liquid because Shopify caps Liquid files at
   256 KB. The section loads this file synchronously right after the drawer markup,
   so it runs at exactly the point the inline script used to.
   Values that need Liquid (theme settings, the current product's tags) are set
   inline by the section on window.GOEYE_CONFIG before this file loads; the
   defaults below only apply if that inline config is missing. */
window.GOEYE_CONFIG = Object.assign({
  prescriptionEmail: 'orders@goeye.in',
  aiPrescriptionEndpoint: 'https://portal.goeye.in/api/ai/prescription',
  aiPrescriptionEnabled: true,
  aiPrescriptionTimeoutMs: 45000,
  productTags: []
}, window.GOEYE_CONFIG || {});

/* ==================================================================== */
/* AI PRESCRIPTION READER                                               */
/* Sends the uploaded prescription to our own VPS endpoint, which calls  */
/* Gemini server-side and returns normalised SPH / CYL / AXIS values.    */
/* Every failure path is soft: the customer keeps the plain file-upload  */
/* flow that existed before.                                            */
/* ==================================================================== */

/** Read a File into a bare base64 string (no data: prefix). */
function goeyeFileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma === -1 ? result : result.slice(comma + 1));
    };
    reader.onerror = () => reject(new Error('Could not read the file'));
    reader.readAsDataURL(file);
  });
}

/** Post the file to the AI endpoint. Resolves to null when AI is unavailable. */
async function goeyeReadPrescriptionWithAI(file) {
  const cfg = window.GOEYE_CONFIG || {};
  if (!cfg.aiPrescriptionEnabled || !cfg.aiPrescriptionEndpoint) return null;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), cfg.aiPrescriptionTimeoutMs || 45000);

  try {
    const data = await goeyeFileToBase64(file);
    const res = await fetch(cfg.aiPrescriptionEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fileName: file.name,
        mimeType: file.type || 'image/jpeg',
        data: data
      }),
      signal: controller.signal
    });

    const json = await res.json().catch(() => null);
    if (!res.ok || !json || json.ok !== true) {
      console.warn('🤖 AI prescription read unavailable:', res.status, json && json.error);
      return null;
    }
    return json;
  } catch (err) {
    console.warn('🤖 AI prescription read failed:', err && err.message);
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Select the option that numerically equals `value`.
 * The Liquid-generated options are strings like "-2.0" / "0.25" while the API
 * returns "-2.00" / "+0.25", so a plain string match would miss.
 */
function goeyeSetSelectByNumber(select, value) {
  if (!select) return false;
  if (value === null || value === undefined || value === '') {
    select.value = '';
    return false;
  }
  const target = parseFloat(value);
  if (!isFinite(target)) return false;

  for (const option of select.options) {
    if (option.value === '') continue;
    if (Math.abs(parseFloat(option.value) - target) < 0.001) {
      select.value = option.value;
      return true;
    }
  }
  return false;
}

/** Format a select's numeric value the way the cart should show it. */
function goeyeFormatRxValue(raw, opts) {
  const withSign = !(opts && opts.noSign);
  if (raw === '' || raw === null || raw === undefined) return null;
  const n = parseFloat(raw);
  if (!isFinite(n)) return null;
  if (!withSign) return String(Math.round(n));
  return (n > 0 ? '+' : n < 0 ? '-' : '') + Math.abs(n).toFixed(2);
}

/** Read whatever is currently in the AI card (including customer edits). */
function goeyeCollectAiRxValues(drawer) {
  const get = (name) => {
    const el = drawer.querySelector('[name="' + name + '"]');
    return el ? el.value : '';
  };
  const eye = (prefix) => ({
    sph: goeyeFormatRxValue(get(prefix + '_sph')),
    cyl: goeyeFormatRxValue(get(prefix + '_cyl')),
    axis: goeyeFormatRxValue(get(prefix + '_axis'), { noSign: true })
  });

  const right = eye('ai_od');
  const left = eye('ai_os');
  if (!right.sph && !left.sph) return null;

  const extras = window.goeyeAiPrescriptionExtras || {};
  return {
    right: right,
    left: left,
    add: extras.add || null,
    pd: extras.pd || null,
    confidence: extras.confidence || null
  };
}

/** Human-readable single-eye summary for the cart line item. */
function goeyeRxEyeString(eye) {
  return 'SPH: ' + (eye.sph || 'N/A') +
         ', CYL: ' + (eye.cyl || 'N/A') +
         ', AXIS: ' + (eye.axis || 'N/A');
}

/** Shared id on lens + frame line items so cart drawer can remove both together */
function goeyeLensFramePairId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'goeye-' + Date.now() + '-' + Math.random().toString(36).slice(2, 11);
}

// Helper: Get main product (frame) variant ID from multiple possible sources
// Main product page uses data-variant-id on Add to Cart button, not select/input[name="id"]
function getMainProductVariantId() {
  // Accept only a positive-integer variant id (guards against empty / NaN /
  // placeholder values that would make /cart/add.js reject with "Cannot find
  // variant").
  const toValidId = (v) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  // 1. Main product Add-to-Cart button — authoritative for THIS product and
  //    kept in sync with the currently selected variant (main-product.liquid
  //    updates its data-variant-id on variant change). Preferred FIRST because
  //    the product page itself has no <input name="id">, so a generic
  //    name="id" lookup would match a quick-add form from another section
  //    (trending / similar products) and return the WRONG product's variant —
  //    or an invalid one, causing "Cannot find variant".
  const addToCartBtn = document.querySelector('#addToCartBtn');
  if (addToCartBtn) {
    const id = toValidId(addToCartBtn.getAttribute('data-variant-id'));
    if (id) return id;
  }
  // 2. Buy Now button.
  const buyNowBtn = document.querySelector('#buyNowBtn, #gokwik-buy-now');
  if (buyNowBtn) {
    const id = toValidId(buyNowBtn.getAttribute('data-variant-id'));
    if (id) return id;
  }
  // 3. window.product (if set by theme).
  if (window.product && window.product.selected_or_first_available_variant) {
    const id = toValidId(window.product.selected_or_first_available_variant.id);
    if (id) return id;
  }
  // 4. Last resort: a traditional cart form variant selector.
  const variantSelector = document.querySelector('form[action*="/cart/add"] select[name="id"], form[action*="/cart/add"] input[name="id"]');
  if (variantSelector) {
    const id = toValidId(variantSelector.value);
    if (id) return id;
  }
  return null;
}

document.addEventListener('DOMContentLoaded', function() {
  // Get the main product variant ID on initialization
  window.mainProductVariantId = getMainProductVariantId();

  // ============================================
  // LENS LOADER - logo animation shown on the lens-selector window
  // while a product is being added to the cart. Self-contained: creates
  // its own overlay inside .la-sheet, no global fetch override.
  // ============================================
  var __lensLoaderCount = 0;
  var __lensLoaderSafety = null;
  var __lensLoaderShownAt = 0;
  var __lensLoaderHideTimer = null;
  // One full rotation of the logo GIF is ~2200ms — keep the overlay visible at
  // least that long (plus a small margin for GIF decode) so the animation is
  // always seen completing a full loop from the start.
  var LENS_LOADER_MIN_MS = 2300;
  var LENS_LOADER_GIF = 'https://cdn.shopify.com/s/files/1/0570/8120/0663/files/giff.gif?v=1786428185';
  // Restart a GIF from frame 0 by replacing the <img> with a fresh clone (loads
  // from cache, so no network). Guarantees a clean 0deg->360deg rotation each show.
  function restartLensGif(img) {
    if (img && img.parentNode) {
      var c = img.cloneNode(true);
      img.parentNode.replaceChild(c, img);
    }
  }
  function ensureLensLoaderEl() {
    var host = document.querySelector('#lens-drawer .la-sheet') || document.getElementById('lens-drawer');
    if (!host) return null;
    var el = host.querySelector('#lensSelectorLoader');
    if (!el) {
      // Make sure the overlay positions against the sheet.
      try {
        var cs = window.getComputedStyle(host);
        if (cs && cs.position === 'static') host.style.position = 'relative';
      } catch (e) {}
      el = document.createElement('div');
      el.id = 'lensSelectorLoader';
      el.setAttribute('aria-hidden', 'true');
      el.style.cssText = 'position:absolute;inset:0;z-index:99999;display:none;align-items:center;justify-content:center;background:rgba(255,255,255,0.9);-webkit-backdrop-filter:blur(2px);backdrop-filter:blur(2px);';
      var img = document.createElement('img');
      img.src = LENS_LOADER_GIF;
      img.alt = 'Adding to cart…';
      img.setAttribute('aria-hidden', 'true');
      img.style.cssText = 'width:480px;max-width:92%;height:auto;display:block;';
      el.appendChild(img);
      host.appendChild(el);
    }
    return el;
  }
  function showLensLoader() {
    var el = ensureLensLoaderEl();
    if (!el) return;
    // A pending "min-time" hide is cancelled by a fresh show.
    if (__lensLoaderHideTimer) { clearTimeout(__lensLoaderHideTimer); __lensLoaderHideTimer = null; }
    __lensLoaderCount++;
    if (el.style.display !== 'flex') {
      __lensLoaderShownAt = Date.now();
      restartLensGif(el.querySelector('img'));
    }
    el.style.display = 'flex';
    if (__lensLoaderSafety) clearTimeout(__lensLoaderSafety);
    __lensLoaderSafety = setTimeout(function () {
      __lensLoaderCount = 0;
      if (__lensLoaderHideTimer) { clearTimeout(__lensLoaderHideTimer); __lensLoaderHideTimer = null; }
      if (el) el.style.display = 'none';
    }, 10000);
  }
  function hideLensLoader(force) {
    var el = document.querySelector('#lens-drawer #lensSelectorLoader');
    if (!el) return;
    if (force) { __lensLoaderCount = 0; }
    else { __lensLoaderCount = Math.max(0, __lensLoaderCount - 1); }
    if (__lensLoaderCount > 0) return;
    var doHide = function () {
      __lensLoaderHideTimer = null;
      el.style.display = 'none';
      if (__lensLoaderSafety) { clearTimeout(__lensLoaderSafety); __lensLoaderSafety = null; }
    };
    // Hold until the logo has shown for at least one full rotation.
    var remain = LENS_LOADER_MIN_MS - (Date.now() - __lensLoaderShownAt);
    if (remain > 0) {
      if (__lensLoaderHideTimer) clearTimeout(__lensLoaderHideTimer);
      __lensLoaderHideTimer = setTimeout(doHide, remain);
    } else {
      doHide();
    }
  }

  // ============================================
  // CART DRAWER HELPER - Unified cart drawer opening
  // ============================================
  async function openCartDrawerAfterAdd() {
    console.log('🛒 Opening cart drawer after add...');
    
    // Use the theme's cart drawer API
    if (window.goeyeCartDrawer && typeof window.goeyeCartDrawer.open === 'function') {
      // First refresh the cart to get fresh data with properties
      if (typeof window.goeyeCartDrawer.refresh === 'function') {
        try {
          await window.goeyeCartDrawer.refresh();
          console.log('🛒 Cart refreshed successfully');
        } catch (err) {
          console.error('Cart refresh error:', err);
        }
      }
      
      // Small delay to ensure DOM updates
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Then open the drawer with fresh content
      window.goeyeCartDrawer.open();
      console.log('🛒 Cart drawer opened');

      // Cart drawer now owns the view — hand off / hide the lens loader.
      hideLensLoader(true);

      // Dispatch events for any other listeners
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
    } else {
      // Fallback to cart page if drawer not available
      console.warn('Cart drawer API not found, redirecting to cart page');
      window.location.href = '/cart';
    }
  }

  // ============================================
  // FITTING FEE HELPER - Auto-add fitting fee for LensAdvizor products
  // ============================================
    if (!window.fittingFeeHelper) {
      window.fittingFeeHelper = {
        variantId: null,
        productHandle: 'fitting-fee',
        
        // Fetch fitting fee product and cache variant ID
        async getVariantId() {
          if (this.variantId) return this.variantId;
          try {
            const response = await fetch(`/products/${this.productHandle}.js`);
            if (response.ok) {
              const product = await response.json();
              if (product.variants && product.variants.length > 0) {
                this.variantId = product.variants[0].id;
                console.log('✅ FITTING FEE: Cached variant ID:', this.variantId);
                return this.variantId;
              }
            }
          } catch (error) {
            console.error('❌ FITTING FEE: Error fetching product:', error);
          }
          return null;
        },
        
        // Check if fitting fee is already in cart
        async isInCart() {
          try {
            const response = await fetch('/cart.js');
            const cart = await response.json();
            return cart.items.some(item => 
              item.sku === 'FITTING FEE' || 
              item.sku === 'FITTING-FEE' ||
              (item.title && (item.title.includes('Fitting Fee') || item.title.includes('FITTING FEE'))) ||
              (item.product_title && (item.product_title.includes('Fitting Fee') || item.product_title.includes('FITTING FEE'))) ||
              (item.properties && item.properties._fitting_fee === 'true') ||
              item.vendor === 'LensAdvizor-Service' ||
              item.product_type === 'Service'
            );
          } catch (error) {
            console.error('❌ FITTING FEE: Error checking cart:', error);
            return false;
          }
        },
        
        // Add fitting fee item to items array if not already in cart
        async addToItems(items) {
          const variantId = await this.getVariantId();
          if (!variantId) {
            console.warn('⚠️ FITTING FEE: Could not get variant ID, skipping');
            return items;
          }
          
          const alreadyInCart = await this.isInCart();
          if (alreadyInCart) {
            console.log('ℹ️ FITTING FEE: Already in cart, skipping');
            return items;
          }
          
          // Add fitting fee to items
          items.push({
            id: variantId,
            quantity: 1,
            properties: {
              '_fitting_fee': 'true',
              '_hidden': 'true'
            }
          });
          // Set global flag so cart drawer knows fitting fee was added
          window.fittingFeeAddedToCart = true;
          console.log('✅ FITTING FEE: Added to cart items, global flag set');
          return items;
        }
      };
      // Pre-fetch fitting fee variant ID
      window.fittingFeeHelper.getVariantId();
    }
    // ============================================

    const drawer = document.getElementById('lens-drawer');
    const sheet = drawer.querySelector('.la-sheet');
    const trigger = document.getElementById('lens-selector-button');
    const backdrop = drawer.querySelector('.la-backdrop');
    const closeBtn = drawer.querySelector('.la-close');
    const steps = [...drawer.querySelectorAll('.la-step')];
    const fill = drawer.querySelector('.la-progress__fill');
    const contents = [...drawer.querySelectorAll('.la-content')];
  
    let currentStep = 1;
    let selectedLensType = null;
    let selectedPower = null;

    // ============================================
    // FRAME-AWARE LENS FILTERING
    // The frame's tags are passed in from Liquid so the lens list can be
    // tailored to the frame type:
    //  - Frames tagged "rimless-lens" cannot take SUPROL lenses, so those are
    //    removed before any lens card is rendered.
    //  - Every other frame must never be offered the rimless-only lenses. Those
    //    products also sit in the standard lens collections, so the rimless lens
    //    collection is looked up once and its products are dropped from any list.
    // ============================================
    const frameProductTags = (function () {
      const raw = window.GOEYE_CONFIG.productTags;
      return Array.isArray(raw) ? raw.map(t => String(t).trim().toLowerCase()) : [];
    })();
    // Frames that get the rimless lens rules carry the "rimless-lens" tag.
    const isRimlessFrame = frameProductTags.includes('rimless-lens');
    if (isRimlessFrame) drawer.classList.add('is-rimless-frame');
    // The store curates drill-mount compatible lenses in this collection.
    const RIMLESS_LENS_COLLECTION = 'rimless-lens';
    // SUPROL lenses are identified by their product title (e.g. "SUPROL - BLUE BLOCK"),
    // matching the rule used for the "Best Selling" badge below. Handles are NOT used
    // because some non-SUPROL lenses carry legacy "suprol-*-copy" handles.
    const SUPROL_LENS_PATTERN = /sup[e]?rol/i;

    // Handles of every published product in the rimless lens collection, fetched
    // lazily and cached for the page. Resolves to an empty Set if the collection
    // is missing (cached) or the request fails (not cached, so the next lens load
    // retries). An empty Set means nothing is excluded.
    let rimlessLensHandlesPromise = null;
    function getRimlessLensHandles() {
      if (!rimlessLensHandlesPromise) {
        rimlessLensHandlesPromise = (async () => {
          try {
            const res = await fetchWithTimeout(`/collections/${encodeURIComponent(RIMLESS_LENS_COLLECTION)}/products.json?limit=250`, 4000);
            if (!res.ok) {
              console.warn('Rimless lens collection not available for exclusion (HTTP', res.status + ')');
              return new Set();
            }
            const json = await res.json();
            const handles = (json.products || []).map(p => String(p && p.handle || '').toLowerCase()).filter(Boolean);
            return new Set(handles);
          } catch (err) {
            console.warn('Could not load the rimless lens collection for exclusion:', err.message);
            rimlessLensHandlesPromise = null;
            return new Set();
          }
        })();
      }
      return rimlessLensHandlesPromise;
    }

    // The store marks zero-power lens products by placing them in its zero-power
    // collections (their tags are identical to with-power lenses). This is the
    // union of those collections' product handles, fetched lazily and cached for
    // the page. A missing collection contributes nothing; a failed request makes
    // the whole lookup resolve to null (not cached, so the next lens load retries).
    const ZERO_POWER_LENS_COLLECTIONS = ['new-zero-power-lenses', 'zero-power-blue-block', 'zero-power-photochromic', 'zero-power-colour-lens', 'zero-power-rimless', 'zero-power-photochromic-rimless'];
    // Decides whether a lens product is a zero-power lens. Membership of a published
    // zero-power collection is the primary signal. When a product is in none of them
    // (collection not yet published to the Online Store, or the feed still cached),
    // fall back to the product text: this store writes a power range on every
    // with-power lens ("Recommended for Low Powers (-5 to +5)", "Power Range - (upto
    // +/-5)", "Recommended for High Power (-10 to +10)") and never on zero-power ones.
    const ZERO_POWER_TEXT_PATTERN = /zero\s*power|no\s*power|\bplano\b/i;
    const WITH_POWER_TEXT_PATTERN = /recommended\s+for\s+(?:low|high)\s+power|power\s*range|[-\u2212\u2013]\s*\d+(?:\.\d+)?\s*to\s*\+?\s*\d+|\+\s*\/\s*-\s*\d|upto\s*\+/i;
    function isZeroPowerLensProduct(product, zeroPowerHandles) {
      const handle = String(product && product.handle || '').toLowerCase();
      if (zeroPowerHandles && zeroPowerHandles.has(handle)) return true;
      const title = String(product && product.title || '');
      const text = String(product && product.body_html || '').replace(/<[^>]*>/g, ' ');
      if (ZERO_POWER_TEXT_PATTERN.test(title) || ZERO_POWER_TEXT_PATTERN.test(text)) return true;
      if (WITH_POWER_TEXT_PATTERN.test(title) || WITH_POWER_TEXT_PATTERN.test(text)) return false;
      return true; // no power statement anywhere: zero power by this store's convention
    }
    let zeroPowerLensHandlesPromise = null;
    function getZeroPowerLensHandles() {
      if (!zeroPowerLensHandlesPromise) {
        zeroPowerLensHandlesPromise = (async () => {
          const handles = new Set();
          let failed = false;
          await Promise.all(ZERO_POWER_LENS_COLLECTIONS.map(async (handle) => {
            try {
              const res = await fetchWithTimeout(`/collections/${encodeURIComponent(handle)}/products.json?limit=250`, 4000);
              if (!res.ok) return;
              const json = await res.json();
              (json.products || []).forEach(p => {
                const h = String(p && p.handle || '').toLowerCase();
                if (h) handles.add(h);
              });
            } catch (err) {
              failed = true;
              console.warn('Could not load zero-power lens collection', handle + ':', err.message);
            }
          }));
          if (failed) {
            zeroPowerLensHandlesPromise = null;
            return null;
          }
          return handles;
        })();
      }
      return zeroPowerLensHandlesPromise;
    }

    async function filterLensesForFrame(products) {
      if (!Array.isArray(products)) return [];
      if (isRimlessFrame) {
        const filtered = products.filter(p => !SUPROL_LENS_PATTERN.test(p && p.title ? String(p.title) : ''));
        if (filtered.length !== products.length) {
          console.log('Rimless frame: hiding', products.length - filtered.length, 'SUPROL lens option(s)');
        }
        return filtered;
      }
      const rimlessHandles = await getRimlessLensHandles();
      if (rimlessHandles.size === 0) return products;
      const filtered = products.filter(p => !rimlessHandles.has(String(p && p.handle || '').toLowerCase()));
      if (filtered.length !== products.length) {
        console.log('Standard frame: hiding', products.length - filtered.length, 'rimless-only lens option(s)');
      }
      return filtered;
    }

    // Collections that feed each lens tab on frames tagged "rimless-lens", per power
    // flow. Each tab lists its sources in order; the first one that has lenses for
    // the flow is shown. Tabs not listed here (Colour Tints) use the standard
    // collections. Nothing falls back to non-rimless lenses: if no source has any,
    // the tab shows "No lenses available".
    const RIMLESS_TAB_COLLECTIONS = {
      'single-vision': {
        bestsellers: ['rimless-lens'],
        'high-power': ['high-power-rimless'],
        photochromic: ['rimless-lens']            // photochromic lenses are picked out of it by title
      },
      'zero-power': {
        bestsellers: ['zero-power-rimless', 'rimless-lens'],
        photochromic: ['zero-power-photochromic-rimless', 'rimless-lens']
      }
    };
    const PHOTOCHROMIC_LENS_PATTERN = /photochrom|transition/i;
    function rimlessSourcesFor(filterType) {
      if (!isRimlessFrame) return [];
      const flow = RIMLESS_TAB_COLLECTIONS[selectedLensType === 'zero-power' ? 'zero-power' : 'single-vision'] || {};
      return Array.isArray(flow[filterType]) ? flow[filterType] : [];
    }
    // Loads the first rimless source that has lenses for the current flow and tab.
    // The "rimless lens" collection mixes with-power and zero-power products, so every
    // source is split with isZeroPowerLensProduct (collection membership, then the
    // product text); the Photochromic tab additionally keeps only photochromic titles.
    async function loadRimlessLenses(sources, filterType) {
      const wantZeroPower = selectedLensType === 'zero-power';
      const zeroPowerHandles = await getZeroPowerLensHandles();
      if (!zeroPowerHandles) console.warn('Zero-power lens collections unavailable; classifying rimless lenses by their descriptions only');
      for (const source of sources) {
        let products = [];
        try {
          const res = await fetchWithTimeout(`/collections/${encodeURIComponent(source)}/products.json?limit=250`, 4000);
          if (!res.ok) {
            console.warn('Rimless collection not available:', source, '(HTTP', res.status + ')');
            continue;
          }
          const json = await res.json();
          products = Array.isArray(json.products) ? json.products : [];
        } catch (err) {
          console.warn('Could not load rimless collection', source + ':', err.message);
          continue;
        }
        const total = products.length;
        products = products.filter(p => isZeroPowerLensProduct(p, zeroPowerHandles) === wantZeroPower);
        if (filterType === 'photochromic') {
          products = products.filter(p => PHOTOCHROMIC_LENS_PATTERN.test(p && p.title ? String(p.title) : ''));
        }
        console.log('Rimless frame:', products.length, 'of', total, 'lens(es) in', source, 'match the', wantZeroPower ? 'zero-power' : 'with-power', filterType, 'tab');
        if (products.length > 0) return { source, products };
      }
      return { source: null, products: [] };
    }

    // Colour-tint lenses are only made for a limited power range. This label is
    // shown to the right of the lens title on colour cards (with-power flow only).
    const COLOUR_LENS_POWER_RANGE_LABEL = '(-3 to +3)';
    let zeroPowerAddInProgress = false; // Prevent multiple add-to-cart when clicking lens card repeatedly
    var photochromicTabStash = null; // When Zero Power: remove Photochromic tab from DOM and stash for re-insert when With Power
    // Rimless frames have their own zero-power photochromic collection, so for them the
    // Photochromic tab stays available in the zero-power flow (other frames hide it).
    function restorePhotochromicTab() {
      if (photochromicTabStash && photochromicTabStash.node && photochromicTabStash.parent) {
        photochromicTabStash.parent.insertBefore(photochromicTabStash.node, photochromicTabStash.insertBefore);
        photochromicTabStash.node._stashed = false;
        photochromicTabStash.node.style.removeProperty('display');
        photochromicTabStash = null;
      }
    }
  
    function validateStep(step) {
      switch(step) {
        case 1:
          const powerTypeSelected = !!drawer.querySelector('.la-power-card.is-selected');
          if (!powerTypeSelected) showError(1);
          return powerTypeSelected;
        case 2:
          const lensSelected = !!drawer.querySelector('.la-lens.is-selected');
          if (!lensSelected) showError(3);
          return lensSelected;
        case 3:
          const hasFile = !!prescriptionData;
          const hasManual = !!window.prescriptionData;
          const hasEmail = !!drawer.querySelector('[data-prescription-method="email"].is-selected');
          const hasPrescription = hasFile || hasManual || hasEmail;
          const hasVariant = !!selectedLensVariantId;
          // Only require prescription for single-vision
          if (selectedLensType === 'single-vision' && !hasPrescription) showError(3);
          if (!hasVariant) showError(3);
          return selectedLensType === 'zero-power' ? hasVariant : (hasPrescription && hasVariant);
        default:
          return false;
      }
    }
  
    function showError(step) {
      const errorMsg = drawer.querySelector(`.la-content--step-${step} .la-error-message`);
      if (errorMsg) {
        errorMsg.hidden = false;
        setTimeout(() => errorMsg.hidden = true, 3000);
      }
    }
  
    function goToStep(n) {
      if (n > currentStep && !validateStep(currentStep)) {
        return;
      }
  
      currentStep = n;
      console.log('🔍 goToStep called:', n);
      
      // Update new step indicators
      const stepIndicators = drawer.querySelectorAll('.la-step-new');
      const stepsContainer = drawer.querySelector('.la-steps-new');
      stepIndicators.forEach((s, index) => {
        const stepNum = index + 1;
        s.classList.toggle('is-active', stepNum === n);
        s.classList.toggle('is-completed', stepNum < n);
      });
      // Update progress line
      if (stepsContainer) {
        stepsContainer.classList.remove('step-2', 'step-3');
        if (n === 2) stepsContainer.classList.add('step-2');
        if (n === 3) stepsContainer.classList.add('step-3');
      }
      // Update progress bar (3 steps = 50% per step)
      if (fill) {
        fill.style.width = ((n-1)*50)+'%';
      }
      
      // Explicitly hide/show content sections - use querySelectorAll to get fresh list
      const allContents = drawer.querySelectorAll('.la-content');
      console.log('🔍 Found content sections:', allContents.length);
      
      allContents.forEach(c => {
        const stepClass = `la-content--step-${n}`;
        const isTargetStep = c.classList.contains(stepClass);
        
        console.log('🔍 Content section:', {
          classes: c.className,
          isTargetStep: isTargetStep,
          currentHidden: c.hasAttribute('hidden')
        });
        
        if (isTargetStep) {
          // Show this step - remove hidden attribute and clear any inline styles
          c.removeAttribute('hidden');
          c.style.display = '';
          c.style.visibility = '';
          c.style.opacity = '';
        } else {
          // Hide all other steps - set hidden attribute
          c.setAttribute('hidden', '');
          // Also set inline styles as backup
          c.style.setProperty('display', 'none', 'important');
        }
      });
      
      // When showing Step 2, sync filter tab visibility for Zero Power (remove Photochromic from DOM)
      if (n === 2) {
        const lensFiltersEl = drawer.querySelector('.la-lens-filters');
        const highPowerFilter = drawer.querySelector('.la-filter-tab[data-filter="high-power"]');
        const photochromicFilter = drawer.querySelector('.la-filter-tab[data-filter="photochromic"]');
        if (selectedLensType === 'zero-power') {
          drawer.classList.add('is-zero-power');
          if (lensFiltersEl) lensFiltersEl.classList.add('is-zero-power');
          if (isRimlessFrame) {
            restorePhotochromicTab();
          } else if (photochromicFilter && !photochromicFilter._stashed) {
            photochromicFilter.style.setProperty('display', 'none', 'important');
            photochromicTabStash = { node: photochromicFilter, parent: photochromicFilter.parentNode, insertBefore: photochromicFilter.nextElementSibling };
            photochromicFilter._stashed = true;
            photochromicFilter.remove();
          }
        } else {
          drawer.classList.remove('is-zero-power');
          if (lensFiltersEl) lensFiltersEl.classList.remove('is-zero-power');
          if (photochromicTabStash && photochromicTabStash.node && photochromicTabStash.parent) {
            photochromicTabStash.parent.insertBefore(photochromicTabStash.node, photochromicTabStash.insertBefore);
            photochromicTabStash.node._stashed = false;
            photochromicTabStash.node.style.removeProperty('display');
            photochromicTabStash = null;
          }
        }
        if (highPowerFilter) {
          if (selectedLensType === 'zero-power') {
            highPowerFilter.style.setProperty('display', 'none', 'important');
            highPowerFilter.setAttribute('aria-hidden', 'true');
          } else {
            highPowerFilter.style.removeProperty('display');
            highPowerFilter.removeAttribute('aria-hidden');
          }
        }
      }
      
      // Special handling for Step 3 when zero-power is selected
      if (n === 3 && selectedLensType === 'zero-power') {
        hidePrescriptionForZeroPower();
      } else if (n === 3) {
        showPrescriptionSection();
        updatePowerSelectorRange();
        // Show only power selection options; hide upload and manual forms until user picks one
        const powerSelectionOptions = drawer.querySelector('.la-power-selection-options');
        const uploadPrescription = drawer.querySelector('.la-upload-prescription');
        const manualPrescription = drawer.querySelector('.la-manual-prescription');
        if (powerSelectionOptions) powerSelectionOptions.style.display = 'block';
        if (uploadPrescription) uploadPrescription.style.display = 'none';
        if (manualPrescription) manualPrescription.style.display = 'none';
      }
      
      // Hide "Save and Continue" button on step 3
      const addToCartBtn = drawer.querySelector('.la-add-to-cart');
      if (addToCartBtn) {
        if (n === 3) {
          addToCartBtn.style.display = 'none';
          addToCartBtn.style.visibility = 'hidden';
        } else {
          addToCartBtn.style.display = '';
          addToCartBtn.style.visibility = '';
        }
      }
      
      console.log('🔍 goToStep completed:', n);
    }
  
   // Update power selector range based on lens type/filter and badge text
   // Lenses with "LOW POWER" or "-5 to +5" badge: -5 to +5 (limited range)
   // Lenses with "HIGH POWER" or "-10 to +10" badge: -10 to +10 (full range)
   function updatePowerSelectorRange() {
     const selectedLensEl = drawer.querySelector('.la-lens.is-selected');
     const lensTitle = (selectedLensEl?.dataset?.productTitle || window.selectedLensProduct?.title || '').toLowerCase();
     
     // Get badge text from the selected lens card wrapper
     let badgeText = '';
     if (selectedLensEl) {
       const cardWrapper = selectedLensEl.closest('.la-lens-card-wrapper');
       const badge = cardWrapper?.querySelector('.la-lens__first-line-badge');
       badgeText = (badge?.textContent || '').toLowerCase();
     }
     
     // Check active filter
     const activeFilter = drawer.querySelector('.la-filter-tab.is-active');
     const activeFilterType = activeFilter ? activeFilter.dataset.filter : '';
     
     // High Power filter = full range (-10 to +10)
     const isHighPowerFilter = activeFilterType === 'high-power';
     
     // Color filter = limited range (-5 to +5)
     const isColorFilter = activeFilterType === 'color';
     
     // Photochromic filter = limited range (-5 to +5)
     const isPhotochromicFilter = activeFilterType === 'photochromic';
     
     // Check if lens title contains color/colour keywords
     const isColoredLens = lensTitle.includes('color') || lensTitle.includes('colour') || 
                           lensTitle.includes('tinted') || lensTitle.includes('sunglass');
     
     // Check if lens title contains photochromic keywords
     const isPhotochromicLens = lensTitle.includes('photochromic') || lensTitle.includes('photochromatic') ||
                                 lensTitle.includes('transition');
     
     // Check badge for power range indication
     const badgeIndicatesLowPower = badgeText.includes('low power') || badgeText.includes('-5 to +5') || badgeText.includes('-5 to + 5');
     const badgeIndicatesHighPower = badgeText.includes('high power') || badgeText.includes('-10 to +10') || badgeText.includes('-10 to + 10');
     
     // Determine SPH range:
     // - Full range (-10 to +10): High Power filter, badge indicates high power
     // - Limited range (-5 to +5): Color/Photochromic filter/lens, or badge indicates low power
     const isLimitedSph = badgeIndicatesLowPower || 
                          (!isHighPowerFilter && !badgeIndicatesHighPower && 
                           (isColorFilter || isColoredLens || isPhotochromicFilter || isPhotochromicLens));
     
     console.log('🔧 updatePowerSelectorRange:', { lensTitle, badgeText, activeFilterType, isHighPowerFilter, badgeIndicatesLowPower, badgeIndicatesHighPower, isColorFilter, isColoredLens, isPhotochromicFilter, isPhotochromicLens, isLimitedSph });
     
     // ai_* ids are the AI reader's review card — it must offer exactly the
     // same power range as the manual form for the selected lens.
     ['od_sph', 'os_sph', 'od_cyl', 'os_cyl',
      'ai_od_sph', 'ai_os_sph', 'ai_od_cyl', 'ai_os_cyl'].forEach(id => {
       const sel = document.getElementById(id);
       if (!sel) return;
       const currentVal = sel.value;
       sel.innerHTML = '<option value="">Select</option>';
       if (/_sph$/.test(sel.name)) {
         if (isLimitedSph) {
           // Limited range: -5.00 to +5.00 in 0.25 increments
           for (let i = -20; i <= 20; i++) {
             const val = (i / 4).toFixed(2);
             const opt = document.createElement('option');
             opt.value = val;
             opt.textContent = val;
             sel.appendChild(opt);
           }
         } else {
           // Full range: -10.00 to +10.00 in 0.25 increments
           for (let i = -40; i <= 40; i++) {
             const val = (i / 4).toFixed(2);
             const opt = document.createElement('option');
             opt.value = val;
             opt.textContent = val;
             sel.appendChild(opt);
           }
         }
       } else {
         // CYL range: -3.00 to 0.00
         for (let i = -12; i <= 0; i++) {
           const val = (i / 4).toFixed(2);
           const opt = document.createElement('option');
           opt.value = val;
           opt.textContent = val;
           sel.appendChild(opt);
         }
       }
       if (currentVal && [...sel.options].some(o => o.value === currentVal)) sel.value = currentVal;
     });
   }

    // Function to set up Save Prescription button handler (can be called multiple times safely)
    function setupSavePrescriptionHandler() {
      const savePrescriptionBtn = drawer.querySelector('.la-save-prescription');
      if (!savePrescriptionBtn) {
        console.warn('⚠️ Save Prescription button not found in drawer');
        return;
      }
      
      console.log('🔧 Setting up Save Prescription button handler');
      
      // Remove existing handler if it exists
      if (savePrescriptionBtn._savePrescriptionHandler) {
        savePrescriptionBtn.removeEventListener('click', savePrescriptionBtn._savePrescriptionHandler);
      }
      
      // Create and attach handler
      savePrescriptionBtn._savePrescriptionHandler = async () => {
        console.log('💾 Save Prescription button clicked');
        
        const od_sph = drawer.querySelector('[name="od_sph"]')?.value || '';
        const od_cyl = drawer.querySelector('[name="od_cyl"]')?.value || '';
        const od_axis = drawer.querySelector('[name="od_axis"]')?.value || '';
        const os_sph = drawer.querySelector('[name="os_sph"]')?.value || '';
        const os_cyl = drawer.querySelector('[name="os_cyl"]')?.value || '';
        const os_axis = drawer.querySelector('[name="os_axis"]')?.value || '';
  
        const prescriptionData = {
          right_eye: {
            sph: od_sph || 'N/A',
            cyl: od_cyl || 'N/A',
            axis: od_axis || 'N/A'
          },
          left_eye: {
            sph: os_sph || 'N/A',
            cyl: os_cyl || 'N/A',
            axis: os_axis || 'N/A'
          }
        };
  
        window.prescriptionData = prescriptionData;
        
        // Format prescription for cart display (matching image 2 format)
        const rightEyeStr = `SPH: ${od_sph || 'N/A'}, CYL: ${od_cyl || 'N/A'}, AXIS: ${od_axis || 'N/A'}`;
        const leftEyeStr = `SPH: ${os_sph || 'N/A'}, CYL: ${os_cyl || 'N/A'}, AXIS: ${os_axis || 'N/A'}`;
        
        console.log('💾 Prescription data:', prescriptionData);
        console.log('💾 Lens product available:', !!window.selectedLensProduct);
        console.log('💾 Lens handle available:', !!window.selectedLensHandle);
        console.log('💾 Main product variant ID:', window.mainProductVariantId);
        
        // Validate main product variant ID - try multiple sources (main product uses data-variant-id, not form inputs)
        if (!window.mainProductVariantId) {
          window.mainProductVariantId = getMainProductVariantId();
          if (window.mainProductVariantId) {
            console.log('💾 Found main product variant ID from fallback:', window.mainProductVariantId);
          }
        }
        // If still not found, try fetching from current product URL
        if (!window.mainProductVariantId) {
          const matches = window.location.pathname.match(/\/products\/([^\/\?#]+)/);
          if (matches && matches[1]) {
            try {
              const productData = await fetch(`/products/${matches[1]}.js`).then(res => res.json());
              if (productData && productData.variants && productData.variants.length > 0) {
                window.mainProductVariantId = productData.variants[0].id;
                if (!window.product) window.product = productData;
                console.log('💾 Found main product variant ID from URL fetch:', window.mainProductVariantId);
              }
            } catch (e) {
              console.warn('Could not fetch product from URL:', e);
            }
          }
        }
        if (!window.mainProductVariantId) {
          alert('Frame product not found. Please refresh the page and try again.');
          return;
        }
        
        // Get selected variant ID from multiple sources
        let lensVariantId = window.selectedLensVariantId || null;
        
        // Try to get from DOM if not available
        if (!lensVariantId) {
          const selectedVariantEl = drawer.querySelector('.la-list--powers .la-power-item.is-selected');
          if (selectedVariantEl) {
            lensVariantId = selectedVariantEl.getAttribute('data-variant-id');
            if (lensVariantId) {
              lensVariantId = parseInt(lensVariantId, 10);
              window.selectedLensVariantId = lensVariantId;
            }
          }
        }
        
        console.log('💾 Selected variant ID:', lensVariantId);
        
        // Check if we have lens product data - if yes, add to cart immediately
        // Use selected variant if available, otherwise use first available variant
        if (window.selectedLensProduct && window.selectedLensHandle) {
          
          // If no variant selected, use first available variant
          if (!lensVariantId && window.selectedLensProduct.variants && window.selectedLensProduct.variants.length > 0) {
            lensVariantId = window.selectedLensProduct.variants[0].id;
            console.log('💾 No variant selected, using first available variant:', lensVariantId);
          }
          
          if (lensVariantId) {
            // Show loading state — spinner + "Adding..." (same animation as the
            // main product Add-to-Cart button). Restored on error.
            const saveBtnOriginalHTML = savePrescriptionBtn.innerHTML;
            try {
              savePrescriptionBtn.disabled = true;
              savePrescriptionBtn.innerHTML = '<div class="la-cta-spinner"></div><span>Adding...</span>';

              // Get lens product data
              const lensProduct = window.selectedLensProduct;
              const lensHandle = window.selectedLensHandle;
              
              // Get lens name
              const selectedLensEl = drawer.querySelector('.la-lens.is-selected');
              const lensTitleEl = selectedLensEl ? selectedLensEl.querySelector('.la-lens__title') : null;
              const lensName = lensTitleEl ? lensTitleEl.textContent.trim() : (lensProduct ? lensProduct.title : 'Lens');
              
              // Get power type
              const selectedPowerCard = drawer.querySelector('.la-power-card.is-selected');
              const powerType = selectedPowerCard ? selectedPowerCard.querySelector('.la-power-title').textContent.trim() : 'With Power';
              
              // Get main product (frame) variant ID
              let mainProductVariantId = window.mainProductVariantId;
              if (!mainProductVariantId) {
                const variantSelector = document.querySelector('select[name="id"], input[name="id"]');
                if (variantSelector) {
                  mainProductVariantId = variantSelector.value;
                } else {
                  const matches = window.location.pathname.match(/\/products\/([^\/\?#]+)/);
                  if (matches && matches[1]) {
                    const productData = await fetch(`/products/${matches[1]}.js`).then(res => res.json());
                    if (productData && productData.variants && productData.variants[0]) {
                      mainProductVariantId = productData.variants[0].id;
                    }
                  }
                }
              }
              
              if (!mainProductVariantId) {
                throw new Error('Cannot find frame product ID');
              }
              
              // Get main product SKU and title
              let mainProductSku = '';
              let mainProductTitle = '';
              try {
                if (window.product && window.product.selected_or_first_available_variant) {
                  mainProductSku = window.product.selected_or_first_available_variant.sku || '';
                  mainProductTitle = window.product.title || '';
                }
                if (!mainProductSku) {
                  const variantSelector = document.querySelector('select[name="id"], input[name="id"]');
                  if (variantSelector && variantSelector.value && window.product) {
                    const variant = window.product.variants.find(v => v.id == variantSelector.value);
                    if (variant) {
                      mainProductSku = variant.sku || '';
                    }
                  }
                }
                if (!mainProductSku || !mainProductTitle) {
                  const matches = window.location.pathname.match(/\/products\/([^\/\?#]+)/);
                  if (matches && matches[1]) {
                    const productData = await fetch(`/products/${matches[1]}.js`).then(res => res.json());
                    if (productData) {
                      if (!mainProductSku && productData.variants) {
                        const currentVariant = productData.variants.find(v => v.id == mainProductVariantId) || productData.variants[0];
                        if (currentVariant) {
                          mainProductSku = currentVariant.sku || '';
                        }
                      }
                      if (!mainProductTitle) mainProductTitle = productData.title || '';
                    }
                  }
                }
                if (!mainProductSku) {
                  mainProductSku = `VAR-${mainProductVariantId}`;
                }
                if (!mainProductTitle) {
                  mainProductTitle = mainProductSku;
                }
              } catch (err) {
                console.error('Error getting main product SKU/title:', err);
                mainProductSku = mainProductSku || `VAR-${mainProductVariantId}`;
                mainProductTitle = mainProductTitle || mainProductSku;
              }
              
              // Create items array for cart with prescription details
              const goeyePairId = goeyeLensFramePairId();
              let items = [
                {
                  id: lensVariantId,
                  quantity: 1,
                  properties: {
                    'Lens Type': powerType || 'With Power',
                    'Power Type': powerType || 'With Power',
                    'Lens Brand': lensName,
                    'Prescription': 'Manual Entry',
                    'Right Eye (OD)': rightEyeStr,
                    'Left Eye (OS)': leftEyeStr,
                    'Frame SKU': mainProductSku,
                    'Associated Frame': mainProductTitle,
                    '_bundle_group_id': goeyePairId
                  }
                },
                {
                  id: mainProductVariantId,
                  quantity: 1,
                  properties: {
                    'Frame': 'With Custom Lenses',
                    '_bundle': 'lens-frame',
                    '_bundle_group_id': goeyePairId
                  }
                }
              ];
              
              // Add fitting fee for LensAdvizor products
              if (window.fittingFeeHelper) {
                items = await window.fittingFeeHelper.addToItems(items);
              }
              
              console.log('💾 Adding items to cart (Save Prescription):', items);
              console.log('💾 Lens variant ID:', items[0]?.id, 'Frame variant ID:', items[1]?.id);
              
              // Validate variant IDs before sending
              if (!items[0]?.id) {
                throw new Error('Lens variant ID is missing. Please select a lens.');
              }
              if (!items[1]?.id) {
                throw new Error('Frame variant ID is missing. Please refresh the page and try again.');
              }

              // Add items to cart
              showLensLoader();
              const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ items })
              });
              
              console.log('💾 Cart response status:', response.status);
              
              if (!response.ok) {
                let errorMessage = 'Failed to add items to cart';
                try {
                  const errorData = await response.json();
                  console.error('💾 Shopify error:', errorData);
                  errorMessage = errorData.description || errorData.message || errorMessage;
                } catch (parseError) {
                  const errorText = await response.text();
                  console.error('💾 Raw error:', errorText);
                }
                throw new Error(errorMessage);
              }
              
              const result = await response.json();
              console.log('💾 Cart success:', result);
              
              // Verify properties were saved by fetching cart
              try {
                const cartResponse = await fetch('/cart.js');
                const cartData = await cartResponse.json();
                console.log('💾 Cart items with properties:', cartData.items.map(item => ({
                  title: item.title,
                  properties: item.properties
                })));
              } catch (e) {
                console.log('Could not verify cart:', e);
              }
              
              // Success state — checkmark + "Added!" (same as main Add-to-Cart)
              savePrescriptionBtn.innerHTML = '<svg viewBox="0 0 24 24" style="width:20px;height:20px;stroke:currentColor;stroke-width:2.5;fill:none"><polyline points="20 6 9 17 4 12"></polyline></svg><span>Added!</span>';

              // Close the lens selector drawer and open cart
              close();
              openCartDrawerAfterAdd();
              // Restore the button label after the drawer closes so it's ready
              // for the next open.
              setTimeout(() => {
                savePrescriptionBtn.innerHTML = saveBtnOriginalHTML;
                savePrescriptionBtn.disabled = false;
              }, 1500);
              return;
            } catch (error) {
              console.error('💾 Error adding to cart:', error);
              alert(error.message || 'Failed to add items to cart. Please try again.');
              savePrescriptionBtn.innerHTML = saveBtnOriginalHTML;
              savePrescriptionBtn.disabled = false;
              return;
            }
          } else {
            console.warn('💾 No lens variant available');
            alert('Please select a lens variant first');
            return;
          }
        } else {
          console.warn('💾 No lens product selected');
          alert('Please select a lens first');
          return;
        }
      };
      
      // Attach the handler
      savePrescriptionBtn.addEventListener('click', savePrescriptionBtn._savePrescriptionHandler);
      console.log('✅ Save Prescription button handler attached');
    }
    
    // Function to set up file upload handler (can be called multiple times safely)
    function setupFileUploadHandler() {
      const fileInput = drawer.querySelector('#upload-prescription');
      const uploadForm = drawer.querySelector('.la-upload-prescription');
      const uploadLabel = drawer.querySelector('.la-upload-label');
      const uploadInfo = drawer.querySelector('.la-upload-info');
      const filename = drawer.querySelector('.la-filename');
      const removeFileBtn = drawer.querySelector('.la-remove-file');
      const uploadProgress = drawer.querySelector('.la-upload-progress');
      // Find the button specifically inside the upload form
      const addToCartBtn = uploadForm ? uploadForm.querySelector('.la-add-to-cart') : drawer.querySelector('.la-add-to-cart');

      // AI reader elements
      const aiReading    = drawer.querySelector('.la-ai-reading');
      const aiResult     = drawer.querySelector('.la-ai-result');
      const aiConfidence = drawer.querySelector('.la-ai-confidence');
      const aiWarnings   = drawer.querySelector('.la-ai-warnings');
      const aiExtras     = drawer.querySelector('.la-ai-extras');
      const aiChipAdd    = drawer.querySelector('.la-ai-chip--add');
      const aiChipPd     = drawer.querySelector('.la-ai-chip--pd');

      if (!fileInput) {
        console.warn('File input not found');
        return;
      }
      
      if (!addToCartBtn) {
        console.warn('Add to cart button not found in upload form');
        return;
      }
      
      // Remove existing handlers if they exist
      if (fileInput._fileChangeHandler) {
        fileInput.removeEventListener('change', fileInput._fileChangeHandler);
      }
      if (addToCartBtn._uploadAddToCartHandler) {
        addToCartBtn.removeEventListener('click', addToCartBtn._uploadAddToCartHandler);
      }
      if (removeFileBtn && removeFileBtn._removeFileHandler) {
        removeFileBtn.removeEventListener('click', removeFileBtn._removeFileHandler);
      }
      
      let prescriptionData = null;

      /** Clear anything the AI reader put on screen or on `window`. */
      function resetAiResult() {
        window.goeyeAiPrescription = null;
        window.goeyeAiPrescriptionExtras = null;
        if (aiReading) aiReading.style.display = 'none';
        if (aiResult) aiResult.style.display = 'none';
        if (aiWarnings) { aiWarnings.style.display = 'none'; aiWarnings.innerHTML = ''; }
        if (aiExtras) aiExtras.style.display = 'none';
        if (aiChipAdd) aiChipAdd.style.display = 'none';
        if (aiChipPd) aiChipPd.style.display = 'none';
        ['ai_od_sph','ai_od_cyl','ai_od_axis','ai_os_sph','ai_os_cyl','ai_os_axis'].forEach((name) => {
          const el = drawer.querySelector('[name="' + name + '"]');
          if (el) el.value = '';
        });
      }

      function resetFileUpload() {
        if (fileInput) fileInput.value = '';
        if (uploadLabel) uploadLabel.style.display = 'block';
        if (uploadInfo) uploadInfo.style.display = 'none';
        if (uploadProgress) uploadProgress.style.display = 'none';
        prescriptionData = null;
        window.prescriptionData = null;
        resetAiResult();
        if (addToCartBtn) {
          addToCartBtn.disabled = true;
          addToCartBtn.style.display = 'none'; // Hide button when file is removed
        }
      }

      /** Reveal the Save and Continue button once a file is in hand. */
      function showContinueButton() {
        if (!addToCartBtn) {
          console.warn('⚠️ Add to cart button not found when trying to show it');
          return;
        }
        addToCartBtn.disabled = false;
        addToCartBtn.style.display = 'flex';
        addToCartBtn.style.visibility = 'visible';
        addToCartBtn.style.opacity = '1';
      }

      /** Paint the AI card with the values Gemini returned. */
      function renderAiResult(result) {
        if (!aiResult || !result || !result.found) return false;

        const right = result.right || {};
        const left = result.left || {};

        // Rebuild the option lists first so the card offers exactly the powers
        // the chosen lens supports (low-power lenses cap SPH at ±5.00).
        try {
          updatePowerSelectorRange();
        } catch (rangeErr) {
          console.warn('Could not apply lens power range to the AI card:', rangeErr);
        }

        const outOfRange = [];
        const apply = (name, value, label) => {
          const ok = goeyeSetSelectByNumber(drawer.querySelector('[name="' + name + '"]'), value);
          // A value the model read but the selected lens cannot be made in.
          if (!ok && value !== null && value !== undefined && value !== '') outOfRange.push(label);
          return ok;
        };

        const filled = [
          apply('ai_od_sph', right.sph, 'right eye SPH ' + right.sph),
          apply('ai_od_cyl', right.cyl, 'right eye CYL ' + right.cyl),
          apply('ai_od_axis', right.axis, 'right eye AXIS ' + right.axis),
          apply('ai_os_sph', left.sph, 'left eye SPH ' + left.sph),
          apply('ai_os_cyl', left.cyl, 'left eye CYL ' + left.cyl),
          apply('ai_os_axis', left.axis, 'left eye AXIS ' + left.axis)
        ].filter(Boolean).length;

        if (!filled) return false;

        // Confidence pill
        if (aiConfidence) {
          const confidence = result.confidence || 'medium';
          aiConfidence.setAttribute('data-confidence', confidence);
          aiConfidence.textContent = confidence === 'high'
            ? 'Clear read'
            : confidence === 'medium' ? 'Please verify' : 'Please double-check';
        }

        // Anything the model or the normaliser flagged
        const warnings = Array.isArray(result.warnings) ? result.warnings.filter(Boolean) : [];
        if (outOfRange.length) {
          warnings.push('The selected lens is not available in ' + outOfRange.join(', ') +
                        '. Please pick a different lens or contact us for a custom power.');
        }
        if (aiWarnings && warnings.length) {
          aiWarnings.innerHTML = '<ul>' + warnings.map((w) => {
            const li = document.createElement('li');
            li.textContent = w;
            return li.outerHTML;
          }).join('') + '</ul>';
          aiWarnings.style.display = 'block';
        }

        // ADD / PD are not part of the dropdowns — show them as read-only chips
        const add = right.add || left.add || null;
        const pd = result.pd || null;
        if (aiChipAdd && add) {
          aiChipAdd.querySelector('b').textContent = add;
          aiChipAdd.style.display = 'inline-block';
        }
        if (aiChipPd && pd) {
          aiChipPd.querySelector('b').textContent = pd + ' mm';
          aiChipPd.style.display = 'inline-block';
        }
        if (aiExtras && (add || pd)) aiExtras.style.display = 'flex';

        window.goeyeAiPrescriptionExtras = {
          add: add,
          pd: pd,
          confidence: result.confidence || null
        };
        window.goeyeAiPrescription = result;

        aiResult.style.display = 'block';
        return true;
      }

      // File input change handler
      fileInput._fileChangeHandler = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
          const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
          if (!validTypes.includes(file.type)) {
            throw new Error('Please upload a PDF or image file (JPG/PNG)');
          }
          if (file.size > 5 * 1024 * 1024) {
            throw new Error('File size must be less than 5MB');
          }

          resetAiResult();
          if (uploadProgress) uploadProgress.style.display = 'block';

          // Store the actual file for Shopify's native upload
          prescriptionData = file;
          window.prescriptionData = file;
          window.prescriptionFileName = file.name;
          window.prescriptionFileObject = file; // Keep reference to actual file

          console.log('📁 File stored for Shopify native upload:', file.name);

          // Show the filename straight away — the AI read runs after it
          if (uploadProgress) uploadProgress.style.display = 'none';
          if (uploadLabel) uploadLabel.style.display = 'none';
          if (uploadInfo) uploadInfo.style.display = 'flex';
          if (filename) filename.textContent = file.name;

          // Read the prescription with AI. Any failure just leaves the
          // customer with the plain upload flow, so this never blocks the sale.
          if (aiReading) aiReading.style.display = 'flex';
          if (addToCartBtn) addToCartBtn.style.display = 'none';

          const result = await goeyeReadPrescriptionWithAI(file);

          if (aiReading) aiReading.style.display = 'none';

          if (result && result.found && renderAiResult(result)) {
            console.log('🤖 AI read prescription:', result);
          } else {
            console.log('🤖 AI could not read power values — continuing with file upload only');
          }

          showContinueButton();

        } catch (error) {
          alert(error.message);
          resetFileUpload();
        }
      };
      
      // Remove file handler
      if (removeFileBtn) {
        removeFileBtn._removeFileHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          resetFileUpload();
        };
        removeFileBtn.addEventListener('click', removeFileBtn._removeFileHandler);
      }
      
      // Add to Cart button handler for upload
      addToCartBtn._uploadAddToCartHandler = async () => {
        if (!prescriptionData) {
          alert('Please upload a prescription file first');
          return;
        }
        
        if (!window.selectedLensProduct || !window.selectedLensHandle) {
          alert('Please select a lens first');
          return;
        }
        
        try {
          // Show loading state
          addToCartBtn.disabled = true;
          addToCartBtn.textContent = 'Adding…';
          
          // Get lens product data
          const lensProduct = window.selectedLensProduct;
          const lensHandle = window.selectedLensHandle;
          
          // Use first available variant if none selected
          let lensVariantId = window.selectedLensVariantId || null;
          if (!lensVariantId && lensProduct.variants && lensProduct.variants.length > 0) {
            lensVariantId = lensProduct.variants[0].id;
            console.log('📁 No variant selected, using first available:', lensVariantId);
          }
          
          if (!lensVariantId) {
            throw new Error('Cannot find lens variant. Please select a lens first.');
          }
          
          console.log('📁 Using lens variant:', lensVariantId);
          
          // Get lens name
          const selectedLensEl = drawer.querySelector('.la-lens.is-selected');
          const lensTitleEl = selectedLensEl ? selectedLensEl.querySelector('.la-lens__title') : null;
          const lensName = lensTitleEl ? lensTitleEl.textContent.trim() : (lensProduct ? lensProduct.title : 'Lens');
          
          // Get power type
          const selectedPowerCard = drawer.querySelector('.la-power-card.is-selected');
          const powerType = selectedPowerCard ? selectedPowerCard.querySelector('.la-power-title').textContent.trim() : 'With Power';
          
          // Get main product (frame) variant ID
          let mainProductVariantId = window.mainProductVariantId;
          if (!mainProductVariantId) {
            const variantSelector = document.querySelector('select[name="id"], input[name="id"]');
            if (variantSelector) {
              mainProductVariantId = variantSelector.value;
            } else {
              const matches = window.location.pathname.match(/\/products\/([^\/\?#]+)/);
              if (matches && matches[1]) {
                const productData = await fetch(`/products/${matches[1]}.js`).then(res => res.json());
                if (productData && productData.variants && productData.variants[0]) {
                  mainProductVariantId = productData.variants[0].id;
                }
              }
            }
          }
          
          if (!mainProductVariantId) {
            throw new Error('Cannot find frame product ID');
          }
          
          // Get main product SKU and title
          let mainProductSku = '';
          let mainProductTitle = '';
          try {
            if (window.product && window.product.selected_or_first_available_variant) {
              mainProductSku = window.product.selected_or_first_available_variant.sku || '';
              mainProductTitle = window.product.title || '';
            }
            if (!mainProductSku) {
              const variantSelector = document.querySelector('select[name="id"], input[name="id"]');
              if (variantSelector && variantSelector.value && window.product) {
                const variant = window.product.variants.find(v => v.id == variantSelector.value);
                if (variant) {
                  mainProductSku = variant.sku || '';
                }
              }
            }
            if (!mainProductSku || !mainProductTitle) {
              const matches = window.location.pathname.match(/\/products\/([^\/\?#]+)/);
              if (matches && matches[1]) {
                const productData = await fetch(`/products/${matches[1]}.js`).then(res => res.json());
                if (productData) {
                  if (!mainProductSku && productData.variants) {
                    const currentVariant = productData.variants.find(v => v.id == mainProductVariantId) || productData.variants[0];
                    if (currentVariant) {
                      mainProductSku = currentVariant.sku || '';
                    }
                  }
                  if (!mainProductTitle) mainProductTitle = productData.title || '';
                }
              }
            }
            if (!mainProductSku) {
              mainProductSku = `VAR-${mainProductVariantId}`;
            }
            if (!mainProductTitle) {
              mainProductTitle = mainProductSku;
            }
          } catch (err) {
            console.error('Error getting main product SKU/title:', err);
            mainProductSku = mainProductSku || `VAR-${mainProductVariantId}`;
            mainProductTitle = mainProductTitle || mainProductSku;
          }
          
          console.log('📁 Adding items to cart with Shopify native file upload');
          console.log('📁 Lens variant ID:', lensVariantId, 'Frame variant ID:', mainProductVariantId);
          
          // Validate variant IDs
          if (!lensVariantId) {
            throw new Error('Lens variant ID is missing. Please select a lens.');
          }
          if (!mainProductVariantId) {
            throw new Error('Frame variant ID is missing. Please refresh the page and try again.');
          }
          
          // Use Shopify's native file upload via hidden form
          // This is the ONLY way to upload files to Shopify's CDN
          const hiddenForm = document.createElement('form');
          hiddenForm.method = 'POST';
          hiddenForm.action = '/cart/add';
          hiddenForm.enctype = 'multipart/form-data';
          hiddenForm.style.display = 'none';
          
          // Add lens item
          const addHiddenInput = (name, value) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            hiddenForm.appendChild(input);
          };
          
          // Values the AI read from the file, including any edits the customer
          // made in the review card. Null when AI was unavailable or unreadable.
          const aiRx = goeyeCollectAiRxValues(drawer);

          const goeyePairIdUpload = goeyeLensFramePairId();
          addHiddenInput('items[0][id]', lensVariantId);
          addHiddenInput('items[0][quantity]', '1');
          addHiddenInput('items[0][properties][Lens Type]', powerType || 'With Power');
          addHiddenInput('items[0][properties][Power Type]', powerType || 'With Power');
          addHiddenInput('items[0][properties][Lens Brand]', lensName);
          addHiddenInput('items[0][properties][Prescription]', aiRx ? 'File Upload (power read from file)' : 'File Upload');
          if (aiRx) {
            addHiddenInput('items[0][properties][Right Eye (OD)]', goeyeRxEyeString(aiRx.right));
            addHiddenInput('items[0][properties][Left Eye (OS)]', goeyeRxEyeString(aiRx.left));
            if (aiRx.add) addHiddenInput('items[0][properties][ADD]', aiRx.add);
            if (aiRx.pd) addHiddenInput('items[0][properties][PD]', aiRx.pd + ' mm');
            // Internal flag (underscore = hidden from the customer-facing cart)
            addHiddenInput('items[0][properties][_rx_source]', 'ai-read:' + (aiRx.confidence || 'unknown'));
          }
          addHiddenInput('items[0][properties][Frame SKU]', mainProductSku);
          addHiddenInput('items[0][properties][Associated Frame]', mainProductTitle);
          addHiddenInput('items[0][properties][_bundle_group_id]', goeyePairIdUpload);
          
          // Add the prescription file - this is the key for Shopify native upload
          if (window.prescriptionFileObject) {
            const fileInput = document.createElement('input');
            fileInput.type = 'file';
            fileInput.name = 'items[0][properties][Prescription File]';
            fileInput.style.display = 'none';
            hiddenForm.appendChild(fileInput);
            
            // Create a DataTransfer to set the file
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(window.prescriptionFileObject);
            fileInput.files = dataTransfer.files;
          }
          
          // Add frame item
          addHiddenInput('items[1][id]', mainProductVariantId);
          addHiddenInput('items[1][quantity]', '1');
          addHiddenInput('items[1][properties][Frame]', 'With Custom Lenses');
          addHiddenInput('items[1][properties][_bundle]', 'lens-frame');
          addHiddenInput('items[1][properties][_bundle_group_id]', goeyePairIdUpload);
          
          // Add return URL to come back after form submission
          addHiddenInput('return_to', 'back');
          
          // Append form to body and submit
          document.body.appendChild(hiddenForm);
          
          // Use fetch with FormData to submit and stay on page
          const formData = new FormData(hiddenForm);

          showLensLoader();
          const response = await fetch('/cart/add', {
            method: 'POST',
            body: formData
          });
          
          // Clean up
          document.body.removeChild(hiddenForm);
          
          console.log('📁 Cart response status:', response.status);
          
          if (!response.ok) {
            let errorMessage = 'Failed to add items to cart';
            try {
              const errorText = await response.text();
              console.error('📁 Cart error:', errorText);
              if (errorText.includes('error')) {
                errorMessage = 'Upload failed. Please try again.';
              }
            } catch (parseError) {
              console.error('Error parsing response:', parseError);
            }
            throw new Error(errorMessage);
          }
          
          console.log('📁 Cart success - items added with prescription file');
          
          // Close the lens selector drawer and open cart
          close();
          openCartDrawerAfterAdd();
          
        } catch (error) {
          console.error('📁 Error adding to cart:', error);
          hideLensLoader(true);
          alert(error.message || 'Failed to add items to cart. Please try again.');
          addToCartBtn.disabled = false;
          addToCartBtn.textContent = 'Save and Continue';
        }
      };
      
      // Attach handlers
      fileInput.addEventListener('change', fileInput._fileChangeHandler);
      addToCartBtn.addEventListener('click', addToCartBtn._uploadAddToCartHandler);
      
      console.log('✅ File upload handler attached');
    }
    
    // Function to set up power selection handlers (can be called multiple times safely)
    function setupPowerSelectionHandlers() {
      const wrap = drawer.querySelector('.la-content--step-3');
      if (!wrap) return;
      
      const powerSelectionContainer = wrap.querySelector('.la-power-selection-options');
      if (!powerSelectionContainer) return;
      
      const list = wrap.querySelector('.la-list--powers');
      const powerSelectionOptions = wrap.querySelector('.la-power-selection-options');
      
      // Remove existing handlers if they exist
      if (powerSelectionContainer._submitLaterHandler) {
        powerSelectionContainer.removeEventListener('click', powerSelectionContainer._submitLaterHandler);
      }
      if (powerSelectionContainer._enterManuallyHandler) {
        powerSelectionContainer.removeEventListener('click', powerSelectionContainer._enterManuallyHandler);
      }
      if (powerSelectionContainer._uploadFileHandler) {
        powerSelectionContainer.removeEventListener('click', powerSelectionContainer._uploadFileHandler);
      }
      
      // Create and attach submit later handler
      powerSelectionContainer._submitLaterHandler = async (e) => {
        const submitLaterOption = e.target.closest('[data-power-option="submit-later"]');
        if (!submitLaterOption) return;
        
        // Prevent multiple clicks
        if (submitLaterOption.hasAttribute('data-processing')) {
          console.log('Already processing, ignoring click');
          return;
        }
        submitLaterOption.setAttribute('data-processing', 'true');
        
        try {
          // Show loading state
          submitLaterOption.style.opacity = '0.6';
          submitLaterOption.style.pointerEvents = 'none';
          
          console.log('🛒 Submit Power Later clicked');
          console.log('Current state:', {
            selectedLensHandle: window.selectedLensHandle,
            selectedLensProduct: !!window.selectedLensProduct,
            selectedLensType: selectedLensType,
            currentStep: currentStep
          });
          
          // Get the selected lens element (might not exist if drawer was reset)
          const selectedLensEl = drawer.querySelector('.la-lens.is-selected');
          
          // Get the selected lens product - prioritize stored data, then element data
          let lensHandle = window.selectedLensHandle;
          let lensProduct = window.selectedLensProduct;
          
          // If we don't have stored data, try to get it from the selected element
          if (!lensHandle && selectedLensEl) {
            lensHandle = selectedLensEl.dataset.productHandle;
            console.log('Got lens handle from selected element:', lensHandle);
          }
          
          // If we still don't have a handle, check if we're in a valid flow
          if (!lensHandle) {
            // Check if we have a selected power type and can determine the lens type
            const selectedPowerCard = drawer.querySelector('.la-power-card.is-selected');
            if (!selectedPowerCard) {
              throw new Error('Please select a power type first');
            }
            throw new Error('Please select a lens first. The lens selection was cleared.');
          }
          
          // If we don't have the product data, fetch it
          if (!lensProduct) {
            console.log('Fetching lens product for handle:', lensHandle);
            try {
              const response = await fetch(`/products/${lensHandle}.js`);
              if (!response.ok) {
                throw new Error(`Failed to fetch lens product: ${response.statusText}`);
              }
              lensProduct = await response.json();
              // Store it for future use
              window.selectedLensProduct = lensProduct;
              window.selectedLensHandle = lensHandle;
              console.log('Fetched and stored lens product:', lensProduct.title);
            } catch (fetchError) {
              console.error('Error fetching lens product:', fetchError);
              throw new Error('Failed to load lens details. Please try again.');
            }
          }
          
          // Use the first available variant for the lens (since no specific power is selected)
          const lensVariantId = lensProduct.variants && lensProduct.variants[0] ? lensProduct.variants[0].id : null;
          
          if (!lensVariantId) {
            throw new Error('Cannot find lens variant');
          }
          
          // Get main product (frame) variant ID
          let mainProductVariantId = window.mainProductVariantId;
          if (!mainProductVariantId) {
            const variantSelector = document.querySelector('select[name="id"], input[name="id"]');
            if (variantSelector) {
              mainProductVariantId = variantSelector.value;
            } else {
              const matches = window.location.pathname.match(/\/products\/([^\/\?#]+)/);
              if (matches && matches[1]) {
                const productData = await fetch(`/products/${matches[1]}.js`).then(res => res.json());
                if (productData && productData.variants && productData.variants[0]) {
                  mainProductVariantId = productData.variants[0].id;
                }
              }
            }
          }
          
          if (!mainProductVariantId) {
            throw new Error('Cannot find frame product ID');
          }
          
          // Get lens and power type info
          const selectedPowerCard = drawer.querySelector('.la-power-card.is-selected');
          const powerType = selectedPowerCard ? selectedPowerCard.querySelector('.la-power-title').textContent.trim() : '';
          const lensTitleEl = selectedLensEl ? selectedLensEl.querySelector('.la-lens__title') : null;
          const lensName = lensTitleEl ? lensTitleEl.textContent.trim() : (lensProduct ? lensProduct.title : 'Lens');
          
          // Get main product SKU and title
          let mainProductSku = '';
          let mainProductTitle = '';
          try {
            if (window.product && window.product.selected_or_first_available_variant) {
              mainProductSku = window.product.selected_or_first_available_variant.sku || '';
              mainProductTitle = window.product.title || '';
            }
            if (!mainProductSku) {
              const variantSelector = document.querySelector('select[name="id"], input[name="id"]');
              if (variantSelector && variantSelector.value && window.product) {
                const variant = window.product.variants.find(v => v.id == variantSelector.value);
                if (variant) {
                  mainProductSku = variant.sku || '';
                }
              }
            }
            if (!mainProductSku || !mainProductTitle) {
              const matches = window.location.pathname.match(/\/products\/([^\/\?#]+)/);
              if (matches && matches[1]) {
                const productData = await fetch(`/products/${matches[1]}.js`).then(res => res.json());
                if (productData) {
                  if (!mainProductSku && productData.variants) {
                    const currentVariant = productData.variants.find(v => v.id == mainProductVariantId) || productData.variants[0];
                    if (currentVariant) {
                      mainProductSku = currentVariant.sku || '';
                    }
                  }
                  if (!mainProductTitle) mainProductTitle = productData.title || '';
                }
              }
            }
            if (!mainProductSku) {
              mainProductSku = `VAR-${mainProductVariantId}`;
            }
            if (!mainProductTitle) {
              mainProductTitle = mainProductSku;
            }
          } catch (err) {
            console.error('Error getting main product SKU/title:', err);
            mainProductSku = mainProductSku || `VAR-${mainProductVariantId}`;
            mainProductTitle = mainProductTitle || mainProductSku;
          }
          
          // Create items array for cart
          const goeyePairIdLater = goeyeLensFramePairId();
          let items = [
            {
              id: lensVariantId,
              quantity: 1,
              properties: {
                'Lens Type': powerType || 'With Power',
                'Power Type': powerType || 'With Power',
                'Lens Brand': lensName,
                'Prescription': 'Submit Power Later',
                'Frame SKU': mainProductSku,
                'Associated Frame': mainProductTitle,
                '_bundle_group_id': goeyePairIdLater
              }
            },
            {
              id: mainProductVariantId,
              quantity: 1,
              properties: {
                'Frame': 'With Custom Lenses',
                '_bundle': 'lens-frame',
                '_bundle_group_id': goeyePairIdLater
              }
            }
          ];
          
          // Add fitting fee for LensAdvizor products
          if (window.fittingFeeHelper) {
            items = await window.fittingFeeHelper.addToItems(items);
          }
          
          console.log('Adding items to cart (Submit Power Later):', items);

          // Add items to cart
          showLensLoader();
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ items })
          });
          
          if (!response.ok) {
            let errorMessage = 'Failed to add items to cart';
            try {
              const errorData = await response.json();
              errorMessage = errorData.description || errorMessage;
            } catch (parseError) {
              console.error('Error parsing response:', parseError);
            }
            throw new Error(errorMessage);
          }
          
          const result = await response.json();
          console.log('Cart success:', result);
          
          // Close the lens selector drawer and open cart
          close();
          openCartDrawerAfterAdd();
          
        } catch (err) {
          console.error('Error adding to cart:', err);
          alert(err.message || 'Failed to add items to cart. Please try again.');
          submitLaterOption.style.opacity = '1';
          submitLaterOption.style.pointerEvents = 'auto';
          submitLaterOption.removeAttribute('data-processing');
        }
      };
      
      // Create and attach enter manually handler
      powerSelectionContainer._enterManuallyHandler = (e) => {
        const enterManuallyOption = e.target.closest('[data-power-option="enter-manually"]');
        if (!enterManuallyOption) return;
        
        // Show manual prescription form directly
        const manualForm = drawer.querySelector('.la-manual-prescription');
        const addToCartBtn = drawer.querySelector('.la-add-to-cart');
        const stepHeader = wrap.querySelector('.la-step-header');
        
        if (powerSelectionOptions) {
          powerSelectionOptions.style.display = 'none';
        }
        if (list) {
          list.style.display = 'none';
        }
        if (stepHeader) {
          stepHeader.style.display = 'none';
        }
        if (manualForm) {
          manualForm.style.display = 'block';
        }
        updatePowerSelectorRange();
        if (addToCartBtn) {
          addToCartBtn.style.display = 'none';
        }
        
        // Enable the Save Prescription button
        const savePrescriptionBtn = drawer.querySelector('.la-save-prescription');
        if (savePrescriptionBtn) {
          savePrescriptionBtn.disabled = false;
          savePrescriptionBtn.style.opacity = '1';
        }
        
        // CRITICAL: Ensure Save Prescription handler is attached when manual form is shown
        setTimeout(() => {
          setupSavePrescriptionHandler();
          console.log('✅ Save Prescription handler attached after Enter Power Manually click');
        }, 100);
      };
      
      // Create and attach upload file handler
      powerSelectionContainer._uploadFileHandler = (e) => {
        const uploadFileOption = e.target.closest('[data-power-option="upload-file"]');
        if (!uploadFileOption) return;
        
        console.log('📁 Upload File option clicked');
        
        // Show upload prescription form
        const uploadForm = drawer.querySelector('.la-upload-prescription');
        const addToCartBtn = drawer.querySelector('.la-add-to-cart');
        const stepHeader = wrap.querySelector('.la-step-header');
        
        if (powerSelectionOptions) {
          powerSelectionOptions.style.display = 'none';
        }
        if (list) {
          list.style.display = 'none';
        }
        if (stepHeader) {
          stepHeader.style.display = 'none';
        }
        if (uploadForm) {
          uploadForm.style.display = 'block';
        }
        // Show the "Save and Continue" button for file upload (initially disabled)
        // Find the button specifically inside the upload form
        const uploadFormButton = uploadForm ? uploadForm.querySelector('.la-add-to-cart') : null;
        const allAddToCartButtons = drawer.querySelectorAll('.la-add-to-cart');
        
        allAddToCartButtons.forEach((btn) => {
          // Check if button is inside the upload form
          if (uploadForm && uploadForm.contains(btn)) {
            btn.style.display = 'flex';
            btn.style.visibility = 'visible';
            btn.style.opacity = '1';
            btn.disabled = true; // Disabled until file is uploaded
            btn.textContent = 'Save and Continue';
            console.log('✅ Upload form button shown (disabled until file upload)');
          } else {
            // Hide all other buttons
            btn.style.display = 'none';
          }
        });
        
        // If button wasn't found, log a warning
        if (!uploadFormButton && uploadForm) {
          console.warn('⚠️ Button not found inside upload form');
        }
        
        // Ensure file upload handler is attached
        setTimeout(() => {
          setupFileUploadHandler();
          console.log('✅ File upload handler attached after Upload File click');
        }, 100);
      };
      
      // Attach all handlers
      powerSelectionContainer.addEventListener('click', powerSelectionContainer._submitLaterHandler);
      powerSelectionContainer.addEventListener('click', powerSelectionContainer._enterManuallyHandler);
      powerSelectionContainer.addEventListener('click', powerSelectionContainer._uploadFileHandler);
    }
  
    function open() { 
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      
      // CRITICAL: Always reset to Step 1 when drawer opens
      // Reset state variables
      selectedLensType = null;
      selectedPower = null;
      currentStep = 1;
      
      // Clear global window variables (keep main product from page for fast add-to-cart)
      window.selectedLensProduct = null;
      window.selectedLensHandle = null;
      window.mainProductVariantId = getMainProductVariantId();
      if (window.product) {
        if (window.product.selected_or_first_available_variant) {
          window._mainProductSkuCache = window.product.selected_or_first_available_variant.sku || '';
        }
        window._mainProductTitleCache = window.product.title || '';
      }
      window.prescriptionData = null;
      window.selectedLensVariantId = null;
      
      // Clear selected states on power cards
      const powerCards = drawer.querySelectorAll('.la-power-card');
      powerCards.forEach(card => {
        card.classList.remove('is-selected');
      });
      
      // Clear selected states on lens cards
      const lensCards = drawer.querySelectorAll('.la-lens');
      lensCards.forEach(card => {
        card.classList.remove('is-selected');
      });
      
      // Clear Zero Power state and re-insert Photochromic tab when drawer opens (reset to initial state)
      drawer.classList.remove('is-zero-power');
      const lensFiltersEl = drawer.querySelector('.la-lens-filters');
      if (lensFiltersEl) lensFiltersEl.classList.remove('is-zero-power');
      if (photochromicTabStash && photochromicTabStash.node && photochromicTabStash.parent) {
        photochromicTabStash.parent.insertBefore(photochromicTabStash.node, photochromicTabStash.insertBefore);
        photochromicTabStash.node._stashed = false;
        photochromicTabStash.node.style.removeProperty('display');
        photochromicTabStash = null;
      }
      // Show High Power filter again when drawer opens (it may have been hidden for Zero Power)
      const highPowerFilter = drawer.querySelector('.la-filter-tab[data-filter="high-power"]');
      if (highPowerFilter) {
        highPowerFilter.style.removeProperty('display');
        highPowerFilter.removeAttribute('aria-hidden');
      }
      // Show Step 3 "Add Power" indicator again when drawer opens (it may have been hidden for Zero Power)
      const step3Indicator = drawer.querySelector('.la-step-new[data-step="3"]');
      if (step3Indicator) {
        step3Indicator.style.display = '';
        step3Indicator.removeAttribute('aria-hidden');
      }
      
      // Reset progress indicator to Step 1 - do this explicitly
      const stepIndicators = drawer.querySelectorAll('.la-step-new');
      stepIndicators.forEach((s, index) => {
        const stepNum = index + 1;
        s.classList.remove('is-active', 'is-completed');
        if (stepNum === 1) {
          s.classList.add('is-active');
        }
      });
      
      const stepsContainer = drawer.querySelector('.la-steps-new');
      if (stepsContainer) {
        stepsContainer.classList.remove('step-2', 'step-3');
      }
      
      // Reset progress bar fill
      const progressFill = drawer.querySelector('.la-progress-fill');
      if (progressFill) {
        progressFill.style.width = '0%';
      }
      
      // Hide all step content except Step 1 - do this FIRST before goToStep
      const allContents = drawer.querySelectorAll('.la-content');
      allContents.forEach(c => {
        if (c.classList.contains('la-content--step-1')) {
          c.removeAttribute('hidden');
          c.style.display = '';
          c.style.visibility = '';
          c.style.opacity = '';
          c.style.setProperty('display', 'block', 'important');
        } else {
          c.setAttribute('hidden', '');
          c.style.setProperty('display', 'none', 'important');
          c.style.visibility = 'hidden';
          c.style.opacity = '0';
        }
      });
      
      // Explicitly ensure step 3 is hidden
      const step3Content = drawer.querySelector('.la-content--step-3');
      if (step3Content) {
        step3Content.setAttribute('hidden', '');
        step3Content.style.setProperty('display', 'none', 'important');
        step3Content.style.visibility = 'hidden';
        step3Content.style.opacity = '0';
      }
      
      // Hide power selection options, manual prescription form, and upload form
      const powerSelectionOptions = drawer.querySelector('.la-power-selection-options');
      if (powerSelectionOptions) {
        powerSelectionOptions.style.display = 'none';
      }
      const manualPrescription = drawer.querySelector('.la-manual-prescription');
      if (manualPrescription) {
        manualPrescription.style.display = 'none';
      }
      const uploadPrescription = drawer.querySelector('.la-upload-prescription');
      if (uploadPrescription) {
        uploadPrescription.style.display = 'none';
      }
      const variantList = drawer.querySelector('.la-list--powers');
      if (variantList) {
        variantList.style.display = 'none';
      }
      
      // Hide all "Save and Continue" buttons
      const allAddToCartButtons = drawer.querySelectorAll('.la-add-to-cart');
      allAddToCartButtons.forEach(btn => {
        btn.style.display = 'none';
      });
      
      // Reset any disabled states on power option cards
      const powerOptionCards = drawer.querySelectorAll('.la-power-option-card');
      powerOptionCards.forEach(card => {
        card.style.opacity = '';
        card.style.pointerEvents = '';
        card.removeAttribute('data-processing');
        card.removeAttribute('disabled');
      });
      
      // Show step header
      const stepHeader = drawer.querySelector('.la-step-header');
      if (stepHeader) {
        stepHeader.style.display = '';
      }
      
      // Clear lens content area
      const lensContent = drawer.querySelector('.la-content--step-2');
      if (lensContent) {
        const lensList = lensContent.querySelector('.la-lens-list');
        if (lensList) {
          lensList.innerHTML = '';
        }
      }
      
      // CRITICAL: Explicitly go to step 1 to ensure proper reset of progress indicators and step visibility
      // Force step 1 to be active
      currentStep = 1;
      
      // Ensure step 3 is hidden BEFORE calling goToStep
      const step3ContentBefore = drawer.querySelector('.la-content--step-3');
      if (step3ContentBefore) {
        step3ContentBefore.setAttribute('hidden', '');
        step3ContentBefore.style.setProperty('display', 'none', 'important');
        step3ContentBefore.style.visibility = 'hidden';
      }
      
      // Ensure step 1 is shown BEFORE calling goToStep
      const step1ContentBefore = drawer.querySelector('.la-content--step-1');
      if (step1ContentBefore) {
        step1ContentBefore.removeAttribute('hidden');
        step1ContentBefore.style.setProperty('display', 'block', 'important');
        step1ContentBefore.style.visibility = 'visible';
        step1ContentBefore.style.opacity = '1';
      }
      
      goToStep(1);
      
      // Double-check step 3 is hidden after goToStep - use multiple checks
      setTimeout(() => {
        const step3Content = drawer.querySelector('.la-content--step-3');
        if (step3Content) {
          step3Content.setAttribute('hidden', '');
          step3Content.style.setProperty('display', 'none', 'important');
          step3Content.style.visibility = 'hidden';
          step3Content.style.opacity = '0';
        }
        const step1Content = drawer.querySelector('.la-content--step-1');
        if (step1Content) {
          step1Content.removeAttribute('hidden');
          step1Content.style.setProperty('display', 'block', 'important');
          step1Content.style.visibility = 'visible';
          step1Content.style.opacity = '1';
        }
        // Ensure currentStep is 1
        currentStep = 1;
        console.log('✅ Drawer opened - Step 1 forced, Step 3 hidden');
      }, 10);
      
      // Set up power selection handlers when drawer opens
      setupPowerSelectionHandlers();
      setupSavePrescriptionHandler();
      setupFileUploadHandler();
      
      // Force visibility for desktop
      if (window.innerWidth >= 481) {
        // Desktop/tablet: the site renders as a centered mobile-width column
        // on a dark background. Keep the lens sheet INSIDE that column at full
        // height (like the phone view) so it never spills outside the used
        // screen. position:absolute fills the fixed .lens-advisor-section
        // (= viewport); left:50% + max-width match the centered body column.
        drawer.style.display = 'block';
        drawer.style.visibility = 'visible';
        drawer.style.opacity = '1';
        drawer.style.zIndex = '99999';

        sheet.style.position = 'absolute';
        sheet.style.top = '0';
        sheet.style.bottom = '0';
        sheet.style.left = '50%';
        sheet.style.right = 'auto';
        sheet.style.transform = 'translateX(-50%)';
        sheet.style.width = '100%';
        sheet.style.maxWidth = 'var(--mobile-max-width, 480px)';
        sheet.style.height = 'auto';
        sheet.style.maxHeight = 'none';
        sheet.style.borderRadius = '0';
        sheet.style.boxShadow = '0 0 40px rgba(0,0,0,.18)';
        sheet.style.padding = '0';
      } else {
        // Mobile: full screen
        drawer.style.display = 'block';
        drawer.style.visibility = 'visible';
        drawer.style.opacity = '1';
        drawer.style.zIndex = '2000';
        
        // Ensure sheet is full screen on mobile
        sheet.style.position = 'fixed';
        sheet.style.top = '0';
        sheet.style.bottom = '0';
        sheet.style.left = '0';
        sheet.style.right = '0';
        sheet.style.width = '100%';
        sheet.style.maxWidth = '100%';
        sheet.style.height = '100vh';
        sheet.style.height = '100dvh'; // Dynamic viewport height
        sheet.style.maxHeight = '100vh';
        sheet.style.maxHeight = '100dvh';
        sheet.style.borderRadius = '0';
        sheet.style.borderTopLeftRadius = '0';
        sheet.style.borderTopRightRadius = '0';
        sheet.style.transform = 'translateY(0)';
        sheet.style.padding = '0';
      }
      
      console.log('✅ Lens drawer opened and reset to Step 1');
    }
    
    // Make functions globally accessible for desktop button
    window.openLensDrawer = function() {
      open();
      goToStep(1);
    };
    
    function close() { 
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      
      // Reset styles for both mobile and desktop
      if (window.innerWidth >= 750) {
        // Desktop: clear all inline styles to let CSS take over
        drawer.style.cssText = '';
        sheet.style.cssText = '';
        const backdrop = drawer.querySelector('.la-backdrop');
        if (backdrop) backdrop.style.cssText = '';
      } else {
        // Mobile: clear all inline styles to let CSS take over
        drawer.style.cssText = '';
        sheet.style.cssText = '';
        const backdrop = drawer.querySelector('.la-backdrop');
        if (backdrop) backdrop.style.cssText = '';
      }
    }
    
    // Make close function globally accessible
    window.closeLensDrawer = close;
  
    // Hide prescription section for zero power lenses
    function hidePrescriptionForZeroPower() {
      const step4Heading = drawer.querySelector('.la-step4-heading');
      const prescriptionHeading = drawer.querySelector('.la-prescription-heading');
      const prescriptionList = drawer.querySelector('.la-list--prescription');
      const manualPrescription = drawer.querySelector('.la-manual-prescription');
      
      if (step4Heading) {
        step4Heading.textContent = 'Select Zero Power Options';
      }
      
      if (prescriptionHeading) {
        prescriptionHeading.style.display = 'none';
      }
      
      if (prescriptionList) {
        prescriptionList.style.display = 'none';
      }
      
      if (manualPrescription) {
        manualPrescription.style.display = 'none';
      }
      
      // Add a notice explaining zero power
      let zeroPowerNotice = drawer.querySelector('.la-zero-power-notice');
      if (!zeroPowerNotice) {
        zeroPowerNotice = document.createElement('div');
        zeroPowerNotice.className = 'la-zero-power-notice';
        zeroPowerNotice.innerHTML = `
          <div class="la-zero-notice-content">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style="margin-right: 12px; flex-shrink: 0;">
              <circle cx="10" cy="10" r="9" fill="#E8F5E8" stroke="#27916D" stroke-width="2"/>
              <path d="M7 10l2 2 4-4" stroke="#27916D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <div>
              <div class="la-notice-title">Zero Power Selected</div>
              <div class="la-notice-subtitle">No prescription needed - these lenses have no corrective power</div>
            </div>
          </div>
        `;
        
        const powersList = drawer.querySelector('.la-list--powers');
        if (powersList && powersList.parentNode) {
          powersList.parentNode.insertBefore(zeroPowerNotice, powersList.nextSibling);
        }
      }
      zeroPowerNotice.style.display = 'block';
    }
    
    // Show prescription section for powered lenses (heading only; forms shown when user picks Upload or Manual)
    function showPrescriptionSection() {
      const step4Heading = drawer.querySelector('.la-step4-heading');
      const prescriptionHeading = drawer.querySelector('.la-prescription-heading');
      const zeroPowerNotice = drawer.querySelector('.la-zero-power-notice');
      
      if (step4Heading) {
        step4Heading.textContent = 'Add Your Power';
      }
      
      if (prescriptionHeading) {
        prescriptionHeading.style.display = '';
      }
      
      if (zeroPowerNotice) {
        zeroPowerNotice.style.display = 'none';
      }
    }
  
    // Event Listeners with null checks
    if (closeBtn) {
      closeBtn.addEventListener('click', close);
    }
    if (trigger) {
      trigger.addEventListener('click', () => { open(); goToStep(1); });
    }
    // Make steps clickable for navigation
    const stepIndicators = drawer.querySelectorAll('.la-step-new');
    stepIndicators.forEach((step, index) => {
      const stepNumber = index + 1;
      step.addEventListener('click', () => {
        // Allow clicking on completed steps or current step
        if (stepNumber <= currentStep || stepNumber === currentStep + 1) {
          goToStep(stepNumber);
        }
      });
    });
  
    // Back button event listeners
    const backToStep1 = drawer.querySelector('.la-back-to-step-1');
    const backToStep2 = drawer.querySelector('.la-back-to-step-2');
    const backToStep3 = drawer.querySelector('.la-back-to-step-3');
  
    if (backToStep1) {
      backToStep1.addEventListener('click', () => goToStep(1));
    }
    if (backToStep2) {
      backToStep2.addEventListener('click', () => goToStep(2));
    }
    if (backToStep3) {
      backToStep3.addEventListener('click', () => goToStep(3));
    }
  
    // Power Type Selection (Step 1)
    drawer.querySelectorAll('.la-power-card').forEach(el => {
      el.addEventListener('click', async () => {
        drawer.querySelectorAll('.la-power-card').forEach(i => i.classList.remove('is-selected'));
        el.classList.add('is-selected');
        selectedLensType = el.dataset.typeHandle;
        
        // Check which type was selected
        if (selectedLensType === 'frame-only') {
          // Handle Frame Only - Add main product to cart and open cart drawer
          await handleFrameOnlySelection(el);
        } else if (selectedLensType === 'single-vision' || selectedLensType === 'zero-power') {
          const lensFiltersEl = drawer.querySelector('.la-lens-filters');
          const highPowerFilter = drawer.querySelector('.la-filter-tab[data-filter="high-power"]');
          const photochromicFilter = drawer.querySelector('.la-filter-tab[data-filter="photochromic"]');
          const allFilterTabs = drawer.querySelectorAll('.la-filter-tab');
          // Zero Power: remove Photochromic tab from DOM so it cannot show; With Power: re-insert it
          if (selectedLensType === 'zero-power') {
            drawer.classList.add('is-zero-power');
            if (lensFiltersEl) lensFiltersEl.classList.add('is-zero-power');
            if (isRimlessFrame) {
              restorePhotochromicTab();
            } else if (photochromicFilter && !photochromicFilter._stashed) {
              photochromicFilter.style.setProperty('display', 'none', 'important');
              photochromicTabStash = { node: photochromicFilter, parent: photochromicFilter.parentNode, insertBefore: photochromicFilter.nextElementSibling };
              photochromicFilter._stashed = true;
              photochromicFilter.remove();
            }
          } else {
            drawer.classList.remove('is-zero-power');
            if (lensFiltersEl) lensFiltersEl.classList.remove('is-zero-power');
            if (photochromicTabStash && photochromicTabStash.node && photochromicTabStash.parent) {
              photochromicTabStash.parent.insertBefore(photochromicTabStash.node, photochromicTabStash.insertBefore);
              photochromicTabStash.node._stashed = false;
              photochromicTabStash.node.style.removeProperty('display');
              photochromicTabStash = null;
            }
          }
          // Show or hide High Power filter: only relevant for "With Power", not for "Zero Power"
          if (highPowerFilter) {
            if (selectedLensType === 'zero-power') {
              highPowerFilter.style.setProperty('display', 'none', 'important');
              highPowerFilter.setAttribute('aria-hidden', 'true');
              // Ensure Bestsellers is active when High Power is hidden
              allFilterTabs.forEach(t => t.classList.remove('is-active'));
              const bestsellersTab = drawer.querySelector('.la-filter-tab[data-filter="bestsellers"]');
              if (bestsellersTab) bestsellersTab.classList.add('is-active');
            } else {
              highPowerFilter.style.removeProperty('display');
              highPowerFilter.removeAttribute('aria-hidden');
            }
          }
          // Hide Step 3 "Add Power" indicator for Zero Power (no prescription needed)
          const step3Indicator = drawer.querySelector('.la-step-new[data-step="3"]');
          if (step3Indicator) {
            if (selectedLensType === 'zero-power') {
              step3Indicator.style.display = 'none';
              step3Indicator.setAttribute('aria-hidden', 'true');
            } else {
              step3Indicator.style.display = '';
              step3Indicator.removeAttribute('aria-hidden');
            }
          }
          // Load default lenses (Bestsellers) and go to Step 2
          await loadLensesByFilter('bestsellers');
          goToStep(2);
          
          // Double-check that Step 1 is hidden and Step 2 filter tabs (Photochromic) stay hidden for Zero Power
          setTimeout(() => {
            const step1Content = drawer.querySelector('.la-content--step-1');
            const step2Content = drawer.querySelector('.la-content--step-2');
            if (step1Content && !step1Content.hasAttribute('hidden')) {
              console.warn('⚠️ Step 1 still visible, forcing hide');
              step1Content.setAttribute('hidden', '');
              step1Content.style.setProperty('display', 'none', 'important');
            }
            if (step2Content && step2Content.hasAttribute('hidden')) {
              console.warn('⚠️ Step 2 still hidden, forcing show');
              step2Content.removeAttribute('hidden');
              step2Content.style.display = '';
            }
            // Re-apply Zero Power: ensure Photochromic tab is removed from DOM
            if (selectedLensType === 'zero-power') {
              drawer.classList.add('is-zero-power');
              const lensFiltersEl = drawer.querySelector('.la-lens-filters');
              const photochromicTab = drawer.querySelector('.la-filter-tab[data-filter="photochromic"]');
              if (lensFiltersEl) lensFiltersEl.classList.add('is-zero-power');
              if (isRimlessFrame) {
                restorePhotochromicTab();
              } else if (photochromicTab && !photochromicTab._stashed) {
                photochromicTab.style.setProperty('display', 'none', 'important');
                photochromicTabStash = { node: photochromicTab, parent: photochromicTab.parentNode, insertBefore: photochromicTab.nextElementSibling };
                photochromicTab._stashed = true;
                photochromicTab.remove();
              }
            }
          }, 100);
        }
      });
    });
  
    // Function to load lenses from a collection based on filter
    let currentCollection = 'all-new-lens';
    let originalProductsHTML = '';
    
    // Helper function to fetch with timeout
    async function fetchWithTimeout(url, timeout = 5000) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      try {
        const response = await fetch(url, { signal: controller.signal });
        clearTimeout(timeoutId);
        return response;
      } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          throw new Error('Request timeout - collection may not exist');
        }
        throw error;
      }
    }
    
    async function loadLensesByFilter(filterType) {
      // Standard frames: start loading the rimless-only lens handles now so the
      // exclusion check runs in parallel with the main collection fetch.
      if (!isRimlessFrame) getRimlessLensHandles();
      else if (rimlessSourcesFor(filterType).length > 0) getZeroPowerLensHandles();
      const lensContainer = drawer.querySelector('.la-list--lenses');
      const loadingMessage = drawer.querySelector('.la-loading-message');
      
      if (!lensContainer) {
        if (loadingMessage) loadingMessage.hidden = true;
        return;
      }
      
      // Set selectedPower based on filter type and lens type
      if (filterType === 'color') {
        // For color lenses, set power based on selected lens type
        if (selectedLensType === 'zero-power') {
          selectedPower = 'colour';
            } else {
          selectedPower = 'colour'; // Default for with-power color lenses
        }
      } else if (filterType === 'photochromic') {
        selectedPower = 'photochromic';
      } else if (filterType === 'high-power') {
        selectedPower = 'anti-glare'; // Default for high power
      } else {
        // For bestsellers, use default based on lens type
        selectedPower = selectedLensType === 'zero-power' ? 'blue-block' : 'anti-glare';
      }
      
      // Show loading message
      if (loadingMessage) loadingMessage.hidden = false;
      lensContainer.style.display = 'none';
      
      try {
        let collectionHandle;
        
        // Determine collection based on filter and lens type
        if (filterType === 'bestsellers') {
          // Zero Power: use New Zero Power Lenses collection; With Power: use all-new-lens
          collectionHandle = selectedLensType === 'zero-power' ? 'new-zero-power-lenses' : 'all-new-lens';
        } else if (filterType === 'high-power') {
          collectionHandle = 'high-power-lens';
        } else if (filterType === 'photochromic') {
          collectionHandle = 'photochromatic-lenses';
        } else if (filterType === 'color') {
          // Zero Power: use zero power colour lens collection; With Power: use with-power colour lens collection
          collectionHandle = selectedLensType === 'zero-power' ? 'zero-power-colour-lens' : 'new-colour-lens-all';
        } else {
          collectionHandle = 'all-new-lens'; // Default
        }

        // Rimless frames: tabs with curated rimless collections read from them instead
        // of the standard collection (see RIMLESS_TAB_COLLECTIONS).
        const rimlessSources = rimlessSourcesFor(filterType);
        const useRimlessCollection = rimlessSources.length > 0;
        if (useRimlessCollection) {
          collectionHandle = rimlessSources[0];
        }
        
        console.log('Loading lenses from collection:', collectionHandle, 'Filter:', filterType);
        
        // Encode the collection handle
        const encodedHandle = encodeURIComponent(collectionHandle.toLowerCase());
        let response;
        
        if (useRimlessCollection) {
          const rimless = await loadRimlessLenses(rimlessSources, filterType);
          if (rimless.source) collectionHandle = rimless.source;
          // Wrap so the shared code below can read the body as usual; an empty list
          // renders the "No lenses available" message.
          response = { ok: true, status: 200, json: async () => ({ products: rimless.products }) };
        } else {
          try {
            response = await fetchWithTimeout(`/collections/${encodedHandle}/products.json?limit=24`, 3000);
          } catch (err) {
            console.log('First collection fetch failed:', err.message);
            response = { ok: false, status: 0 };
          }
        }
        
        // If bestsellers filter fails for Zero Power, try alternative collection names
        if (!response.ok && filterType === 'bestsellers' && selectedLensType === 'zero-power') {
          const alternatives = ['new-zero-power-lenses', 'new-zero-power-lens', 'zero-power-lenses'];
          for (const alt of alternatives) {
            try {
              const altEncoded = encodeURIComponent(alt.toLowerCase());
              const altResponse = await fetchWithTimeout(`/collections/${altEncoded}/products.json?limit=24`, 2000);
              if (altResponse.ok) {
                console.log('Found zero power lenses in collection:', alt);
                response = altResponse;
                collectionHandle = alt;
                break;
              }
            } catch (err) {
              console.log('Collection not found:', alt, err.message);
              continue;
            }
          }
        }
        // If color filter fails, try alternative collection names (with shorter timeout)
        if (!response.ok && filterType === 'color') {
          console.log('Trying alternative collection name for color lenses...');
          const alternatives = selectedLensType === 'zero-power'
            ? ['zero-power-colour-lens', 'zero-power-color-lens', 'zero-power-colour-lenses']
            : [
                'colour-lens-all', 
                'color-lens-all', 
                'new-colour-lens', 
                'colour-lenses'
              ];
          for (const alt of alternatives) {
            try {
              const altEncoded = encodeURIComponent(alt.toLowerCase());
              const altResponse = await fetchWithTimeout(`/collections/${altEncoded}/products.json?limit=24`, 2000);
              if (altResponse.ok) {
                console.log('Found color lenses in collection:', alt);
                response = altResponse;
                collectionHandle = alt;
                break;
              }
            } catch (err) {
              console.log('Collection not found:', alt, err.message);
              continue;
            }
          }
        }
        
        if (!response.ok) {
          console.error('Failed to load collection:', collectionHandle, 'Status:', response.status);
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        data.products = await filterLensesForFrame(data.products || []);
        console.log('Loaded products:', data.products ? data.products.length : 0, 'products');
        
        // Clear existing options
        lensContainer.innerHTML = '';
        
        if (!data.products || data.products.length === 0) {
          lensContainer.innerHTML = '<p style="text-align: center; padding: 2rem; color: #6b7280;">No lenses available for this selection.</p>';
          if (loadingMessage) loadingMessage.hidden = true;
          lensContainer.style.display = '';
          lensContainer.style.visibility = 'visible';
          return;
        }
        
        // Store original HTML for Bestsellers (first load)
        if (filterType === 'bestsellers' && !originalProductsHTML) {
          // Will be set after first render
        }
        
        // Render lens cards
        const escapeHtml = (s) => String(s == null ? '' : s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
        // Helper: get numeric price for sorting - use raw value for comparison (works for paise or rupees)
        const getProductSortPrice = (p) => {
          const toNum = (v) => {
            if (v == null || v === undefined) return null;
            const n = parseFloat(v);
            return !isNaN(n) && n > 0 ? n : null;
          };
          let raw = null;
          if (filterType === 'color') {
            if (p.price_max != null) raw = toNum(p.price_max);
            if (raw == null && p.variants && p.variants.length > 0) {
              const arr = p.variants.map(v => parseFloat(v.price)).filter(x => !isNaN(x) && x > 0);
              if (arr.length > 0) raw = Math.max(...arr);
            }
            if (raw == null && p.price != null) raw = toNum(p.price);
          } else {
            if (p.price_min != null) raw = toNum(p.price_min);
            if (raw == null && p.variants && p.variants.length > 0) {
              const arr = p.variants.map(v => parseFloat(v.price)).filter(x => !isNaN(x) && x > 0);
              if (arr.length > 0) raw = Math.min(...arr);
            }
            if (raw == null && p.price != null) raw = toNum(p.price);
          }
          return raw != null ? raw : Infinity;
        };
        // Sort with-power lens cards by price low to high (all filters: bestsellers, high-power, etc.)
        if (selectedLensType !== 'zero-power' && selectedLensType !== 'frame-only') {
          data.products = [...data.products].sort((a, b) => getProductSortPrice(a) - getProductSortPrice(b));
        }
        data.products.forEach(product => {
          const productImage = product.images && product.images.length > 0 
            ? product.images[0].src
            : product.featured_image && product.featured_image.src 
              ? product.featured_image.src 
              : 'https://cdn.shopify.com/s/files/1/0570/8120/0663/files/placeholder.png';
          
          // Get product price - use the lowest variant price or product price
          // Shopify products.json may return prices in paise (e.g. 240000 for ₹2400) or already in rupees (e.g. 2400)
          let productPrice = null;
          
          // Helper: convert API price to display rupees (handles both paise and already-rupees)
          const convertPrice = (priceValue) => {
            if (!priceValue || priceValue === null || priceValue === undefined) return null;
            const price = parseFloat(priceValue);
            if (isNaN(price) || price <= 0) return null;
            // If value >= 10000, assume paise (e.g. 240000 → 2400); else assume already rupees (e.g. 2400 → 2400)
            const rupees = price >= 10000 ? Math.round(price / 100) : Math.round(price);
            return rupees.toString();
          };
          
          // Parse product description (body_html) into an array of feature strings for card display
          const parseDescriptionToFeatures = (bodyHtml) => {
            if (!bodyHtml || typeof bodyHtml !== 'string') return [];
            const div = document.createElement('div');
            div.innerHTML = bodyHtml;
            let items = [];
            const lis = div.querySelectorAll('li');
            if (lis && lis.length > 0) {
              items = Array.from(lis).map(el => el.textContent.trim()).filter(s => s.length > 0);
            } else {
              const ps = div.querySelectorAll('p');
              if (ps && ps.length > 0) {
                // Handle <br> tags within paragraphs - split by <br> and flatten
                items = [];
                Array.from(ps).forEach(p => {
                  // Replace <br> tags with a unique delimiter before getting text
                  const pHtml = p.innerHTML;
                  const splitByBr = pHtml.split(/<br\s*\/?>/gi);
                  if (splitByBr.length > 1) {
                    // Has <br> tags, split into separate items
                    splitByBr.forEach(part => {
                      const tempDiv = document.createElement('div');
                      tempDiv.innerHTML = part;
                      const text = (tempDiv.textContent || tempDiv.innerText || '').trim();
                      if (text.length > 0) items.push(text);
                    });
                  } else {
                    // No <br> tags, use the whole paragraph text
                    const text = p.textContent.trim();
                    if (text.length > 0) items.push(text);
                  }
                });
              } else {
                // No <li> or <p> tags, try to handle raw HTML with <br> tags
                const htmlStr = div.innerHTML;
                const splitByBr = htmlStr.split(/<br\s*\/?>/gi);
                if (splitByBr.length > 1) {
                  splitByBr.forEach(part => {
                    const tempDiv = document.createElement('div');
                    tempDiv.innerHTML = part;
                    const text = (tempDiv.textContent || tempDiv.innerText || '').trim();
                    if (text.length > 0) items.push(text);
                  });
                } else {
                  const text = div.innerText || div.textContent || '';
                  items = text.split(/\n+/).map(s => s.trim()).filter(s => s.length > 0);
                }
              }
            }
            return items.slice(0, 6);
          };
          
          // For color lenses, use price_max or find the highest variant price
          // This ensures we get the actual product price (₹699) instead of a low variant price (₹7)
          if (filterType === 'color') {
            // First try product.price_max (maximum price across all variants)
            // CRITICAL: Validate that price_max is reasonable (at least ₹10 = 1000 cents) before using it
            if (product.price_max !== null && product.price_max !== undefined) {
              const priceMax = parseFloat(product.price_max);
              // Only use price_max if it's reasonable (at least ₹10 = 1000 cents)
              // This prevents showing ₹7 (700 cents) even if price_max is incorrectly set
              if (!isNaN(priceMax) && priceMax > 0 && priceMax >= 1000) {
                productPrice = convertPrice(priceMax);
              } else if (!isNaN(priceMax) && priceMax > 0 && priceMax < 1000) {
                // price_max exists but is suspiciously low, skip it and use variant calculation instead
                console.warn('⚠️ product.price_max is suspiciously low for color lens:', product.title, 'price_max:', priceMax, 'skipping and using variants');
              }
            }
            
            // If price_max didn't work, try variants - use the maximum price
            // CRITICAL: Filter out suspiciously low prices (₹10 or less = 1000 cents or less) BEFORE calculating max
            if (!productPrice && product.variants && product.variants.length > 0) {
              const allPrices = product.variants
                .map(v => {
                  const price = parseFloat(v.price);
                  return !isNaN(price) && price > 0 ? price : null;
                })
                .filter(p => p !== null)
                // Filter out suspiciously low prices (less than ₹10 = 1000 cents)
                // This prevents showing ₹7 (700 cents) before the correct price loads
                .filter(p => p >= 1000);
              
              if (allPrices.length > 0) {
                // Use maximum price for color lenses (should be ₹699, not ₹7)
                const maxPrice = Math.max(...allPrices);
                productPrice = convertPrice(maxPrice);
              } else {
                // If all prices were filtered out as too low, try without the filter as fallback
                // But still prefer higher prices
                const fallbackPrices = product.variants
                  .map(v => {
                    const price = parseFloat(v.price);
                    return !isNaN(price) && price > 0 ? price : null;
                  })
                  .filter(p => p !== null);
                
                if (fallbackPrices.length > 0) {
                  const maxPrice = Math.max(...fallbackPrices);
                  productPrice = convertPrice(maxPrice);
                }
              }
            }
            
            // Fallback to product.price if still no price (but only if it's reasonable)
            if (!productPrice && product.price !== null && product.price !== undefined) {
              const productPriceValue = parseFloat(product.price);
              // Only use product.price if it's reasonable (at least ₹10 = 1000 cents)
              if (!isNaN(productPriceValue) && productPriceValue >= 1000) {
                productPrice = convertPrice(product.price);
              }
            }
          } else {
            // For other filter types, use the standard logic
            // Priority 1: Use product.price_min (this is the minimum price across all variants in cents)
            if (product.price_min !== null && product.price_min !== undefined) {
              productPrice = convertPrice(product.price_min);
            }
            
            // Priority 2: If price_min didn't work, try variants
            if (!productPrice && product.variants && product.variants.length > 0) {
              // Find the lowest price variant
              const prices = product.variants
                .map(v => {
                  const price = parseFloat(v.price);
                  return !isNaN(price) && price > 0 ? price : null;
                })
                .filter(p => p !== null);
              
              if (prices.length > 0) {
                const minPrice = Math.min(...prices);
                productPrice = convertPrice(minPrice);
              }
            }
          }
          
          // Priority 3: Fallback to product.price (but validate for color lenses)
          if (!productPrice && product.price !== null && product.price !== undefined) {
            if (filterType === 'color') {
              const productPriceValue = parseFloat(product.price);
              // Only use product.price if it's reasonable (at least ₹10 = 1000 cents)
              if (!isNaN(productPriceValue) && productPriceValue >= 1000) {
                productPrice = convertPrice(product.price);
              }
            } else {
              productPrice = convertPrice(product.price);
            }
          }
          
          // CRITICAL: Final validation for color lenses - ensure price is reasonable before displaying
          // If price is still suspiciously low (₹10 or less), don't display it yet
          if (filterType === 'color' && productPrice) {
            const priceValue = parseFloat(productPrice);
            if (!isNaN(priceValue) && priceValue <= 10) {
              console.warn('⚠️ Final price validation failed for color lens:', product.title, 'Price:', productPrice, 'Will fetch individual product');
              // Don't set productPrice to empty, but mark it for correction
              // The correction fetch below will handle updating it
            }
          }
          
          // Debug logging for price calculation (only for first few products to avoid spam)
          if (filterType === 'color' && data.products.indexOf(product) < 3) {
            console.log('🔍 Price Debug - Product:', product.title, {
              'product.price': product.price,
              'product.price_min': product.price_min,
              'product.price_max': product.price_max,
              'all_variants': product.variants?.map(v => ({
                id: v.id,
                title: v.title,
                price: v.price,
                price_in_rupees: v.price ? Math.round(parseFloat(v.price) / 100) : 'N/A',
                compare_at_price: v.compare_at_price,
                sku: v.sku
              })),
              'calculated_price': productPrice ? `₹${productPrice}` : 'N/A',
              'filter_type': filterType
            });
          }
          
          // If still no price, set to empty string (will be handled in template)
          if (!productPrice || productPrice === 'NaN' || productPrice === '0') {
            productPrice = '';
          }
          
          // GoEye products should show actual price, not FREE
          const isGoEyeProduct = false; // Disabled - show actual price
          
          const freeBadge = ''; // Disabled FREE badge
          
          // Ensure selectedPower has a value
          const powerOption = selectedPower || (selectedLensType === 'zero-power' ? 'blue-block' : 'with-power');
          
          // Special card structure for color lenses
          if (filterType === 'color') {
            // Color mapping for different lens colors
            const productTitleLower = product.title.toLowerCase();
            let colorLensText = 'Color Lenses';
            let colorLensIconColor = '#ef4444'; // Default red
            
            // Detect color from product title and set appropriate text and icon color
            if (productTitleLower.includes('light red')) {
              colorLensText = 'Light Pink Lenses';
              colorLensIconColor = '#f8b4d9'; // Light pink
            } else if (productTitleLower.includes('red')) {
              colorLensText = 'Red Color Lenses';
              colorLensIconColor = '#ef4444'; // Red
            } else if (productTitleLower.includes('blue')) {
              colorLensText = 'Blue Color Lenses';
              colorLensIconColor = '#3b82f6'; // Blue
            } else if (productTitleLower.includes('green')) {
              colorLensText = 'Green Color Lenses';
              colorLensIconColor = '#22c55e'; // Green
            } else if (productTitleLower.includes('yellow')) {
              colorLensText = 'Yellow Color Lenses';
              colorLensIconColor = '#eab308'; // Yellow
            } else if (productTitleLower.includes('orange')) {
              colorLensText = 'Orange Color Lenses';
              colorLensIconColor = '#f97316'; // Orange
            } else if (productTitleLower.includes('purple') || productTitleLower.includes('violet')) {
              colorLensText = 'Purple Color Lenses';
              colorLensIconColor = '#a855f7'; // Purple
            } else if (productTitleLower.includes('pink')) {
              colorLensText = 'Pink Color Lenses';
              colorLensIconColor = '#ec4899'; // Pink
            } else if (productTitleLower.includes('brown')) {
              colorLensText = 'Brown Color Lenses';
              colorLensIconColor = '#92400e'; // Brown
            } else if (productTitleLower.includes('black') || productTitleLower.includes('grey') || productTitleLower.includes('gray')) {
              colorLensText = 'Black/Grey Color Lenses';
              colorLensIconColor = '#374151'; // Dark gray/black
            } else if (productTitleLower.includes('white')) {
              colorLensText = 'White Color Lenses';
              colorLensIconColor = '#f3f4f6'; // Light gray/white
            } else {
              // Default fallback
              colorLensText = 'Color Lenses';
              colorLensIconColor = '#ef4444'; // Default red
            }
            
            const firstVariantId = product.variants && product.variants[0] ? product.variants[0].id : '';
            const colorDescriptionFeatures = parseDescriptionToFeatures(product.body_html || '');
            let colorFirstLine = '';
            if (colorDescriptionFeatures.length > 0 && selectedLensType !== 'zero-power') {
              const firstItem = colorDescriptionFeatures[0];
              const lineMatch = firstItem.match(/^(.+?\))\s*(.*)$/s) || firstItem.match(/^([^\n]+)/);
              colorFirstLine = lineMatch ? (lineMatch[1] || lineMatch[0]).trim() : firstItem.split(/\n+/)[0]?.trim() || '';
            }
            const colorFirstLineBadge = colorFirstLine ? `<div class="la-lens__first-line-badge">${escapeHtml(colorFirstLine)}</div>` : '';
            // Power-range note beside the title. Zero-power lenses have no power, so skip it there.
            const colourPowerRangeHtml = selectedLensType !== 'zero-power'
              ? `<span class="la-lens__power-range">${escapeHtml(COLOUR_LENS_POWER_RANGE_LABEL)}</span>`
              : '';
            const lensCard = `
              <div class="la-lens-card-wrapper">
                ${colorFirstLineBadge}
              <div class="la-lens la-lens--color js-lens-card" data-power-option="${powerOption}" data-product-handle="${product.handle}" data-variant-id="${firstVariantId}" data-product-title="${escapeHtml(product.title)}">
                <div class="la-lens__main-content">
                  <div class="la-lens__left-section">
                    <div class="la-lens__color-swatch">
                      <img 
                        src="${productImage}"
                        alt="${product.title}"
                        class="la-lens__color-image"
                        width="80"
                        height="80"
                        onerror="this.onerror=null; this.src='data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 100 100%27%3E%3Ccircle cx=%2750%27 cy=%2750%27 r=%2745%27 fill=%27%23f1f1f1%27 stroke=%27%23ddd%27 stroke-width=%272%27/%3E%3C/svg%3E';"
                      >
                    </div>
                  </div>
                  <div class="la-lens__content">
                    <div class="la-lens__title-row">
                      <div class="la-lens__title la-lens__title--color">${product.title}</div>
                      ${colourPowerRangeHtml}
                    </div>
                    <div class="la-lens__features-wrapper">
                      <div class="la-lens__features">
                        <div class="la-lens__feature-item">
                          <svg class="la-feature-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" fill="${colorLensIconColor}"/>
                            <circle cx="12" cy="12" r="4" fill="#fff"/>
                          </svg>
                          <span>${colorLensText}</span>
                        </div>
                        <div class="la-lens__feature-item">
                          <img class="la-feature-icon" src="https://cdn.shopify.com/s/files/1/0570/8120/0663/files/sg_visual.webp?v=1769586260" alt="Enhanced Depth Perception" width="14" height="14" style="display: block;"/>
                          <span>Enhanced Depth Perception</span>
                        </div>
                        <div class="la-lens__feature-item">
                          <img class="la-feature-icon" src="https://cdn.shopify.com/s/files/1/0570/8120/0663/files/Reduces_overallsunlight_intensity__1.webp?v=1769586260" alt="Protects from Harmful UV Rays" width="14" height="14" style="display: block;"/>
                          <span>Protects from Harmful UV Rays</span>
                        </div>
                      </div>
                    </div>
                    <div class="la-lens__divider"></div>
                    <div class="la-lens__footer">
                      <div class="la-lens__price-wrapper">
                        <span class="la-lens__price-label">Price : </span>
                        ${(!productPrice || parseFloat(productPrice) === 0) ? '<span class="la-lens__price">FREE</span>' : `<span class="la-lens__price">₹${productPrice}</span>`}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div class="la-lens__select">
                  <div class="la-select-circle">
                    <svg class="la-check-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </div>
                </div>
                
                <div class="la-lens__details-popup" hidden>
                  <div class="la-popup__content">
                    <button type="button" class="la-popup__close" aria-label="Close details">×</button>
                    <div class="la-popup__header">
                      <div class="la-popup__brand-image">
                        <img src="${productImage}" alt="${product.title}">
                      </div>
                      <div class="la-popup__title-wrap">
                        <h4 class="la-popup__title">${product.title}</h4>
                        ${colorFirstLine ? `<div class="la-popup__recommended">${escapeHtml(colorFirstLine)}</div>` : ''}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              </div>
            `;
            lensContainer.insertAdjacentHTML('beforeend', lensCard);
            return; // Continue to next product
          }
          
          // Default card structure for other lens types - same design as colored lenses card
          const descriptionFeatures = parseDescriptionToFeatures(product.body_html || '');
          // First line only in badge (With Power only - no badge for Zero Power); rest in feature list
          let firstLine = '';
          let remainingFeatures = descriptionFeatures;
          if (descriptionFeatures.length > 0 && selectedLensType !== 'zero-power') {
            const firstItem = descriptionFeatures[0];
            const lineMatch = firstItem.match(/^(.+?\))\s*(.*)$/s) || firstItem.match(/^([^\n]+)/);
            firstLine = lineMatch ? (lineMatch[1] || lineMatch[0]).trim() : firstItem.split(/\n+/)[0]?.trim() || '';
            const restOfFirst = lineMatch && lineMatch[2] ? [lineMatch[2].trim()].filter(Boolean) : firstItem.split(/\n+/).slice(1).map(s => s.trim()).filter(Boolean);
            remainingFeatures = [...restOfFirst, ...descriptionFeatures.slice(1)];
          }
          const featuresHtml = remainingFeatures.length > 0
            ? remainingFeatures.map(f => `
                <div class="la-lens__feature-item">
                  <img class="la-feature-icon" src="https://cdn.shopify.com/s/files/1/0570/8120/0663/files/tick-mark.png?v=1777965408" alt="" width="15" height="15">
                  <span>${escapeHtml(f)}</span>
                </div>`).join('')
            : '';
          const firstLineBadge = (firstLine && selectedLensType !== 'zero-power') ? `<div class="la-lens__first-line-badge">${escapeHtml(firstLine)}</div>` : '';
          // "Best Selling" pill for SUPROL lens cards.
          const bestSellingBadge = /suprol/i.test(product.title || '')
            ? '<span class="la-lens__bestseller"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l2.9 6.26L21.6 9.27l-4.8 4.68 1.13 6.6L12 17.5l-5.93 3.12 1.13-6.6-4.8-4.68 6.7-.99L12 2z"/></svg>Best Selling</span>'
            : '';
          const showFreeAsPrice = !productPrice || parseFloat(productPrice) === 0;
          const priceDisplayHtml = showFreeAsPrice ? '<span class="la-lens__price">FREE</span>' : `<span class="la-lens__price">₹${productPrice}</span>`;
          const firstVariantId = product.variants && product.variants[0] ? product.variants[0].id : '';
          const lensCard = `
            <div class="la-lens-card-wrapper">
              ${firstLineBadge}
            <div class="la-lens la-lens--color js-lens-card" data-power-option="${powerOption}" data-product-handle="${product.handle}" data-variant-id="${firstVariantId}" data-product-title="${escapeHtml(product.title)}">
              <div class="la-lens__main-content">
                <div class="la-lens__left-col">
                  ${bestSellingBadge}
                  <div class="la-lens__left-section">
                    <div class="la-lens__color-swatch">
                      <img
                        src="${productImage}"
                        alt="${product.title}"
                        class="la-lens__color-image"
                        width="80"
                        height="80"
                        onerror="this.onerror=null; this.src='data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 36 36%27%3E%3Crect width=%2736%27 height=%2736%27 fill=%27%23f1f1f1%27/%3E%3Cpath d=%27M12 6h12v24H12z%27 fill=%27%23ddd%27/%3E%3C/svg%3E';"
                      >
                    </div>
                  </div>
                </div>
                <div class="la-lens__content">
                  <div class="la-lens__title la-lens__title--color">${product.title}</div>
                  <div class="la-lens__features-wrapper">
                    <div class="la-lens__features">
                      ${featuresHtml}
                    </div>
                  </div>
                  <div class="la-lens__divider"></div>
                  <div class="la-lens__footer">
                    <div class="la-lens__price-wrapper">
                      <span class="la-lens__price-label">Price : </span>
                      ${priceDisplayHtml}
                    </div>
                  </div>
                </div>
              </div>
              
              <div class="la-lens__select">
                <div class="la-select-circle">
                  <svg class="la-check-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
              </div>
  
              <div class="la-lens__details-popup" hidden>
                <div class="la-popup__content">
                  <button type="button" class="la-popup__close" aria-label="Close details">×</button>
                  <div class="la-popup__header">
                    <div class="la-popup__brand-image">
                      <img src="${productImage}" alt="${product.title}">
                    </div>
                    <div class="la-popup__title-wrap">
                      <h4 class="la-popup__title">${product.title}</h4>
                      ${firstLine ? `<div class="la-popup__recommended">${escapeHtml(firstLine)}</div>` : ''}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>
          `;
          lensContainer.insertAdjacentHTML('beforeend', lensCard);
        });
        
        // Sort lens cards by price (low to high) - DOM-level sort as fallback
        if (selectedLensType !== 'zero-power' && selectedLensType !== 'frame-only') {
          const wrappers = Array.from(lensContainer.querySelectorAll('.la-lens-card-wrapper'));
          const parsePriceFromEl = (el) => {
            const priceEl = el.querySelector('.la-lens__price');
            if (!priceEl) return Infinity;
            const text = priceEl.textContent || '';
            if (/free/i.test(text)) return 0;
            const match = text.replace(/[^\d.]/g, '');
            const n = parseFloat(match);
            return !isNaN(n) && n > 0 ? n : Infinity;
          };
          wrappers.sort((a, b) => parsePriceFromEl(a) - parsePriceFromEl(b));
          wrappers.forEach(w => lensContainer.appendChild(w));
        }
        
        // Store original HTML for Bestsellers
        if (filterType === 'bestsellers') {
          originalProductsHTML = lensContainer.innerHTML;
        }
        
        // Reattach event listeners for new lens cards
        attachLensCardListeners();
        
        // Hide loading message and show lens container
        if (loadingMessage) loadingMessage.hidden = true;
        lensContainer.style.display = '';
        lensContainer.style.visibility = 'visible';
        lensContainer.style.opacity = '1';
        
        console.log('Lens container displayed with', data.products.length, 'products');
        
        currentCollection = collectionHandle;
        
      } catch (error) {
        console.error('Error loading lenses:', error);
        console.error('Filter type:', filterType);
        console.error('Collection handle attempted:', typeof collectionHandle !== 'undefined' ? collectionHandle : 'unknown');
        
        // Always hide loading message on error
        if (loadingMessage) {
          loadingMessage.hidden = true;
        }
        
        // Show error message
        lensContainer.innerHTML = `<p style="text-align: center; padding: 2rem; color: #ef4444;">Error loading lenses. Please try again.<br><small style="color: #9ca3af;">${error.message}</small></p>`;
        lensContainer.style.display = '';
        lensContainer.style.visibility = 'visible';
        lensContainer.style.opacity = '1';
      } finally {
        // Ensure loading message is always hidden
        if (loadingMessage) {
          loadingMessage.hidden = true;
        }
      }
    }
    
    // Filter button click handlers
    const filterTabs = drawer.querySelectorAll('.la-filter-tab');
    filterTabs.forEach(tab => {
      tab.addEventListener('click', async () => {
        // Update active tab
        filterTabs.forEach(t => t.classList.remove('is-active'));
        tab.classList.add('is-active');
        
        const filterValue = tab.dataset.filter;
        await loadLensesByFilter(filterValue);
      });
    });
  
    // Hide Photochromic tab on init – show only when "With Power" is selected
    var photochromicTabInit = drawer.querySelector('.la-filter-tab[data-filter="photochromic"]');
    if (photochromicTabInit && !photochromicTabInit._stashed) {
      photochromicTabInit.style.setProperty('display', 'none', 'important');
      photochromicTabStash = { node: photochromicTabInit, parent: photochromicTabInit.parentNode, insertBefore: photochromicTabInit.nextElementSibling };
      photochromicTabInit._stashed = true;
      photochromicTabInit.remove();
    }
  
    // Close the drawer when clicking the backdrop.
    backdrop.addEventListener('click', close);
  
    // 🛒 Frame Only Selection Handler
    async function handleFrameOnlySelection(element) {
      try {
        // Show loading state
        const titleElement = element.querySelector('.la-power-title');
        const originalTitle = titleElement ? titleElement.textContent : 'Frame Only';
        if (titleElement) {
        titleElement.textContent = 'Adding to Cart...';
        }
        element.style.opacity = '0.7';
        element.style.pointerEvents = 'none';
  
        // Get the main product variant ID
        let mainProductVariantId = window.mainProductVariantId;
        
        if (!mainProductVariantId) {
          // Try to get the main product variant ID
          const variantSelector = document.querySelector('select[name="id"], input[name="id"]');
          if (variantSelector) {
            mainProductVariantId = variantSelector.value;
          } else {
            // Use URL to extract product ID if needed
            const matches = window.location.pathname.match(/\/products\/([^\/\?#]+)/);
            if (matches && matches[1]) {
              try {
                const productData = await fetch(`/products/${matches[1]}.js`).then(res => res.json());
                if (productData && productData.variants && productData.variants[0]) {
                  mainProductVariantId = productData.variants[0].id;
                }
              } catch (err) {
                console.error('Error fetching product data:', err);
              }
            }
          }
          
          if (!mainProductVariantId) {
            throw new Error('Cannot find frame product ID');
          }
          
          window.mainProductVariantId = mainProductVariantId;
        }
  
        console.log('🛒 SNIPPETS: Adding frame only to cart with variant ID:', mainProductVariantId);
  
        // Get frame SKU for identification
        let frameSku = '';
        try {
          // Method 1: Try to get SKU from current product data
          if (window.product && window.product.selected_or_first_available_variant) {
            frameSku = window.product.selected_or_first_available_variant.sku || '';
          }
          
          // Method 2: Try to get from variant selector if not found
          if (!frameSku) {
            const variantSelector = document.querySelector('select[name="id"], input[name="id"]');
            if (variantSelector && variantSelector.value && window.product) {
              const variant = window.product.variants.find(v => v.id == variantSelector.value);
              if (variant) {
                frameSku = variant.sku || '';
              }
            }
          }
          
          // Method 3: Fetch from API if still not found
          if (!frameSku) {
            const matches = window.location.pathname.match(/\/products\/([^\/\?#]+)/);
            if (matches && matches[1]) {
              try {
                const productData = await fetch(`/products/${matches[1]}.js`).then(res => res.json());
                if (productData && productData.variants) {
                  const currentVariant = productData.variants.find(v => v.id == mainProductVariantId) || productData.variants[0];
                  if (currentVariant) {
                    frameSku = currentVariant.sku || '';
                  }
                }
              } catch (err) {
                console.error('Error fetching frame SKU:', err);
              }
            }
          }
          
          // Fallback: use variant ID if no SKU found
          if (!frameSku) {
            frameSku = `VAR-${mainProductVariantId}`;
          }
          
        } catch (err) {
          console.error('Error getting frame SKU:', err);
          frameSku = `VAR-${mainProductVariantId}`;
        }
  
        // Add frame to cart
        showLensLoader();
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [{
              id: mainProductVariantId,
              quantity: 1,
              properties: {
                '_frame_only': 'true',
                'Frame': 'Frame Only - No Lenses',
                'Frame SKU': frameSku,
                'Product Type': 'Frame Only',
                '_display_order': '1'
              }
            }]
          })
        });
  
        if (!response.ok) {
          const errorData = await response.json();
          console.error('Cart error response:', errorData);
          throw new Error(errorData.description || 'Failed to add frame to cart');
        }
        
        const result = await response.json();
        console.log('🛒 SNIPPETS: Frame added to cart successfully:', result);
  
        // Show success state
        titleElement.textContent = '✅ Added Successfully!';
  
        // Close the lens selector drawer
        setTimeout(() => {
          close();
        }, 150);

        // Update cart and open cart drawer
        try {
          const cartResponse = await fetch('/cart.js');
          const cartData = await cartResponse.json();
          
          // Calculate visible count (excluding fitting fees) for consistent display
          let visibleCount = 0;
          if (cartData && cartData.items) {
            for (const item of cartData.items) {
              const isFittingFee = 
                (item.properties && item.properties._fitting_fee === 'true') ||
                item.sku === 'FITTING-FEE' ||
                item.sku === 'FITTING FEE' ||
                (item.title && (item.title.includes('Fitting Fee') || item.title.includes('FITTING FEE'))) ||
                (item.product_title && (item.product_title.includes('Fitting Fee') || item.product_title.includes('FITTING FEE'))) ||
                item.vendor === 'LensAdvizor-Service' ||
                item.product_type === 'Service';
              if (!isFittingFee) {
                visibleCount += item.quantity;
              }
            }
          }
          
          // Update cart count elements with VISIBLE count (excluding fitting fees)
          const cartCountElements = document.querySelectorAll('.cart-count, .cart-count-bubble, .product-cart-count-bubble, .collection-cart-count-bubble');
          cartCountElements.forEach(element => {
            element.textContent = visibleCount || '';
          });
          
          // Trigger cart refresh events
          document.dispatchEvent(new CustomEvent('cart:updated', { 
            detail: { cart: cartData }
          }));
          document.dispatchEvent(new CustomEvent('cart:refresh', { 
            detail: { cart: cartData }
          }));
          document.dispatchEvent(new CustomEvent('cart:items-changed', { 
            detail: { cart: cartData }
          }));
          
          console.log('🛒 SNIPPETS: Cart updated and refresh events triggered');
        } catch (error) {
          console.error('🛒 SNIPPETS: Error updating cart count:', error);
        }
  
        // Close lens selector and open cart drawer
        close();
        openCartDrawerAfterAdd();

      } catch (error) {
        console.error('🛒 SNIPPETS: Frame only selection error:', error);
        alert(error.message || 'Failed to add frame to cart. Please try again.');
        
        // Restore original state
        const titleElement = element.querySelector('.la-power-title');
        if (titleElement) {
        titleElement.textContent = originalTitle;
        }
        element.style.opacity = '';
        element.style.pointerEvents = '';
        element.classList.remove('is-selected');
      }
    }
  
    // Header back button handler
    const headerBackBtn = drawer.querySelector('.la-back-btn');
    if (headerBackBtn) {
      headerBackBtn.addEventListener('click', () => {
        if (currentStep === 1) {
          // From step 1 the back arrow exits the lens-selector.
          close();
        } else if (currentStep === 3) {
          const powerSelectionOptions = drawer.querySelector('.la-power-selection-options');
          const manualPrescription = drawer.querySelector('.la-manual-prescription');
          const uploadPrescription = drawer.querySelector('.la-upload-prescription');
          const stepHeader = drawer.querySelector('.la-step-header');
          const list = drawer.querySelector('.la-list--powers');
          const isInStep3SubView = (manualPrescription && manualPrescription.style.display === 'block') ||
            (uploadPrescription && uploadPrescription.style.display === 'block');
          if (isInStep3SubView) {
            if (powerSelectionOptions) powerSelectionOptions.style.display = 'block';
            if (manualPrescription) manualPrescription.style.display = 'none';
            if (uploadPrescription) uploadPrescription.style.display = 'none';
            if (stepHeader) stepHeader.style.display = '';
            if (list) list.style.display = 'none';
            const addToCartBtn = drawer.querySelector('.la-add-to-cart');
            if (addToCartBtn) addToCartBtn.style.display = 'none';
          } else {
            goToStep(2);
          }
        } else {
          goToStep(currentStep - 1);
        }
      });
    }
  
    // Old power type selection code - REMOVED (now handled in Step 1)
    // The following code is kept for backward compatibility but may need adjustment
    drawer.querySelectorAll('.la-item[data-step-target="2"]').forEach(el => {
      el.addEventListener('click', async () => {
        drawer.querySelectorAll('.la-item').forEach(i => i.classList.remove('is-selected'));
        el.classList.add('is-selected');
        selectedPower = el.dataset.power;
  
        // Get collection handle based on lens type
        let collectionHandle;
        if (selectedLensType === 'zero-power') {
          // Use zero power collections
          switch(selectedPower) {
            case 'blue-block':
              collectionHandle = 'zero-power-blue-block';
              break;
            case 'photochromic':
              collectionHandle = 'zero-power-photochromic';
              break;
            case 'colour':
              collectionHandle = 'zero-power-colour-lens';
              break;
          }
        } else {
          // Use with-power collections
          switch(selectedPower) {
            case 'anti-glare':
              collectionHandle = 'with-power-anti-glare';
              break;
            case 'blue-block':
              collectionHandle = 'with-power-blue-block';
              break;
            case 'photochromic':
              collectionHandle = 'with-power-photochromic-1';
              break;
            case 'colour':
              collectionHandle = 'with-power-colour-lens';
              break;
          }
        }
        console.log('DEBUG - Power type clicked:', {
          power: selectedPower,
          lensType: selectedLensType,
          collectionHandle: collectionHandle,
          element: el.outerHTML
        });
        
        // Fetch products from collection if handle exists
        if (collectionHandle) {
          try {
            // Encode the collection handle to handle spaces and special characters
            const encodedHandle = encodeURIComponent(collectionHandle.toLowerCase());
            console.log('DEBUG - Fetching collection:', {
              originalHandle: collectionHandle,
              encodedHandle: encodedHandle,
              url: `/collections/${encodedHandle}/products.json`
            });
            
            const response = await fetch(`/collections/${encodedHandle}/products.json?limit=24`);
            console.log('DEBUG - Response:', {
              status: response.status,
              ok: response.ok,
              statusText: response.statusText
            });
            
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            console.log('DEBUG - Collection data:', data);
            data.products = await filterLensesForFrame(data.products || []);
            
            // Update the lens options in step 3
            const lensContainer = drawer.querySelector('.la-list--lenses');
            lensContainer.innerHTML = ''; // Clear existing options
            
            if (!data.products || data.products.length === 0) {
              console.log('DEBUG - No products found in collection');
              lensContainer.innerHTML = '<p>No lenses available for this selection.</p>';
              return;
            }
            
            const parseDescToFeatures = (bodyHtml) => {
              if (!bodyHtml || typeof bodyHtml !== 'string') return [];
              const div = document.createElement('div');
              div.innerHTML = bodyHtml;
              let items = [];
              const lis = div.querySelectorAll('li');
              if (lis && lis.length > 0) {
                items = Array.from(lis).map(el => el.textContent.trim()).filter(s => s.length > 0);
              } else {
                const ps = div.querySelectorAll('p');
                if (ps && ps.length > 0) {
                  items = Array.from(ps).map(el => el.textContent.trim()).filter(s => s.length > 0);
                } else {
                  const text = div.innerText || div.textContent || '';
                  items = text.split(/\n+/).map(s => s.trim()).filter(s => s.length > 0);
                }
              }
              return items.slice(0, 6);
            };
            const escapeForHtml = (s) => String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
            // Sort with-power lens cards by price low to high
            const getSortPrice = (p) => {
              const toNum = (v) => { if (v == null) return null; const n = parseFloat(v); return !isNaN(n) && n > 0 ? n : null; };
              let raw = toNum(p.price_min);
              if (raw == null && p.variants && p.variants.length > 0) {
                const arr = p.variants.map(v => parseFloat(v.price)).filter(x => !isNaN(x) && x > 0);
                if (arr.length > 0) raw = Math.min(...arr);
              }
              if (raw == null) raw = toNum(p.price);
              return raw != null ? raw : Infinity;
            };
            if (selectedLensType !== 'zero-power') {
              data.products = [...data.products].sort((a, b) => getSortPrice(a) - getSortPrice(b));
            }
            
            data.products.forEach(product => {
              console.log('Product data:', {
                title: product.title,
                description: product.description,
                body_html: product.body_html,
                full_product: product
              });
              
              const productImage = product.images && product.images.length > 0 
                ? product.images[0].src
                : product.featured_image && product.featured_image.src 
                  ? product.featured_image.src 
                  : 'https://cdn.shopify.com/s/files/1/0570/8120/0663/files/placeholder.png';
              
              // GoEye products should show actual price, not FREE
              const isGoEyeProduct = false; // Disabled - show actual price
              const freeBadge = ''; // Disabled FREE badge
              
              const toRupees = (raw) => {
                if (raw == null || isNaN(raw) || raw <= 0) return null;
                const n = parseFloat(raw);
                return n >= 10000 ? Math.round(n / 100) : Math.round(n);
              };
              let productPrice = '';
              if (product.price_min != null) {
                const p = toRupees(parseFloat(product.price_min));
                if (p != null && p > 0) productPrice = p.toString();
              }
              if (!productPrice && product.variants && product.variants.length > 0) {
                const prices = product.variants.map(v => parseFloat(v.price)).filter(p => !isNaN(p) && p > 0);
                if (prices.length > 0) {
                  const minRaw = Math.min(...prices);
                  const p = toRupees(minRaw);
                  if (p != null) productPrice = p.toString();
                }
              }
              
              const descriptionFeatures = parseDescToFeatures(product.body_html || '');
              let firstLine = '';
              let remainingFeatures = descriptionFeatures;
              if (descriptionFeatures.length > 0 && selectedLensType !== 'zero-power') {
                const firstItem = descriptionFeatures[0];
                const lineMatch = firstItem.match(/^(.+?\))\s*(.*)$/s) || firstItem.match(/^([^\n]+)/);
                firstLine = lineMatch ? (lineMatch[1] || lineMatch[0]).trim() : firstItem.split(/\n+/)[0]?.trim() || '';
                const restOfFirst = lineMatch && lineMatch[2] ? [lineMatch[2].trim()].filter(Boolean) : firstItem.split(/\n+/).slice(1).map(s => s.trim()).filter(Boolean);
                remainingFeatures = [...restOfFirst, ...descriptionFeatures.slice(1)];
              }
              const featuresHtml = remainingFeatures.length > 0
                ? remainingFeatures.map(f => `
                    <div class="la-lens__feature-item">
                      <img class="la-feature-icon" src="https://cdn.shopify.com/s/files/1/0570/8120/0663/files/tick-mark.png?v=1777965408" alt="" width="15" height="15">
                      <span>${escapeForHtml(f)}</span>
                    </div>`).join('')
                : '';
              const firstLineBadge = (firstLine && selectedLensType !== 'zero-power') ? `<div class="la-lens__first-line-badge">${escapeForHtml(firstLine)}</div>` : '';
              // "Best Selling" pill for SUPROL lens cards.
              const bestSellingBadge = /suprol/i.test(product.title || '')
                ? '<span class="la-lens__bestseller"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l2.9 6.26L21.6 9.27l-4.8 4.68 1.13 6.6L12 17.5l-5.93 3.12 1.13-6.6-4.8-4.68 6.7-.99L12 2z"/></svg>Best Selling</span>'
                : '';
              const showFreeAsPrice = !productPrice || parseFloat(productPrice) === 0;
              const priceDisplayHtml = showFreeAsPrice ? '<span class="la-lens__price">FREE</span>' : `<span class="la-lens__price">₹${productPrice}</span>`;
              const firstVariantId = product.variants && product.variants[0] ? product.variants[0].id : '';
              const lensCard = `
                <div class="la-lens-card-wrapper">
                  ${firstLineBadge}
                <div class="la-lens la-lens--color js-lens-card" data-power-option="${selectedPower}" data-product-handle="${product.handle}" data-variant-id="${firstVariantId}" data-product-title="${escapeForHtml(product.title)}">
                  <div class="la-lens__main-content">
                    <div class="la-lens__left-col">
                      ${bestSellingBadge}
                      <div class="la-lens__left-section">
                        <div class="la-lens__color-swatch">
                          <img
                            src="${productImage}"
                            alt="${product.title}"
                            class="la-lens__color-image"
                            width="80"
                            height="80"
                            onerror="this.onerror=null; this.src='data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 36 36%27%3E%3Crect width=%2736%27 height=%2736%27 fill=%27%23f1f1f1%27/%3E%3Cpath d=%27M12 6h12v24H12z%27 fill=%27%23ddd%27/%3E%3C/svg%3E';"
                          >
                        </div>
                      </div>
                    </div>
                    <div class="la-lens__content">
                      <div class="la-lens__title la-lens__title--color">${product.title}</div>
                      <div class="la-lens__features-wrapper">
                        <div class="la-lens__features">
                          ${featuresHtml}
                        </div>
                      </div>
                      <div class="la-lens__divider"></div>
                      <div class="la-lens__footer">
                        <div class="la-lens__price-wrapper">
                          <span class="la-lens__price-label">Price : </span>
                          ${priceDisplayHtml}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="la-lens__select">
                    <div class="la-select-circle">
                      <svg class="la-check-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </div>
                  </div>
                  <div class="la-lens__details-popup" hidden>
                    <div class="la-popup__content">
                      <button type="button" class="la-popup__close" aria-label="Close details">×</button>
                      <div class="la-popup__header">
                        <div class="la-popup__brand-image">
                          <img src="${productImage}" alt="${product.title}">
                        </div>
                        <div class="la-popup__title-wrap">
                          <h4 class="la-popup__title">${product.title}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                </div>
              `;
              lensContainer.insertAdjacentHTML('beforeend', lensCard);
            });
  
            // Sort lens cards by price (low to high) - DOM-level sort
            if (selectedLensType !== 'zero-power') {
              const wrappers = Array.from(lensContainer.querySelectorAll('.la-lens-card-wrapper'));
              const parsePriceFromEl = (el) => {
                const priceEl = el.querySelector('.la-lens__price');
                if (!priceEl) return Infinity;
                const text = priceEl.textContent || '';
                if (/free/i.test(text)) return 0;
                const match = text.replace(/[^\d.]/g, '');
                const n = parseFloat(match);
                return !isNaN(n) && n > 0 ? n : Infinity;
              };
              wrappers.sort((a, b) => parsePriceFromEl(a) - parsePriceFromEl(b));
              wrappers.forEach(w => lensContainer.appendChild(w));
            }
  
            // Reattach event listeners for new lens cards
            attachLensCardListeners();
          } catch (error) {
            console.error('Error fetching collection products:', error);
            const lensContainer = drawer.querySelector('.la-list--lenses');
            lensContainer.innerHTML = '<p>Error loading lenses. Please try again.</p>';
          }
        } else {
          console.log('No collection handle found for power type:', selectedPower);
        }
  
        goToStep(2);
      });
    });
  
    // Inject the "View More Details" button into each lens card so the
    // user can open a bottom-sheet drawer with the full feature list.
    // The button is appended after .la-lens__features-wrapper, before
    // the divider. Idempotent — won't add twice if the card already
    // has one.
    function injectViewMoreButton(card) {
      if (!card) return;
      var content = card.querySelector('.la-lens__content');
      if (!content) return;

      var featuresWrap = content.querySelector('.la-lens__features-wrapper');
      if (!featuresWrap) return;

      // Only show the button when the card has 3+ feature items (no
      // point opening a drawer that lists the same 2 items already
      // visible). Always show for color lenses where the bottom sheet
      // includes the product description body.
      // Don't show "View Details" on photochromic or coloured lenses.
      var powerOption = card.dataset.powerOption || '';
      if (powerOption === 'photochromic' || powerOption === 'colour') return;

      var featureCount = featuresWrap.querySelectorAll('.la-lens__feature-item').length;
      var hasFullDetails = card.classList.contains('la-lens--color');

      // Never show "View Details" when the card has no feature items — a
      // card with no product description (e.g. the zero-power Photochromatic
      // Lenses card) would only open an empty drawer.
      if (featureCount === 0) return;

      // Insert the button in normal flow, directly BELOW the features list
      // (before the divider), instead of pinning it to the card's top-right.
      if (!card.querySelector('.js-details-trigger') && (featureCount >= 3 || hasFullDetails)) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'la-lens__view-more js-details-trigger';
        btn.setAttribute('aria-haspopup', 'dialog');
        btn.innerHTML =
          '<span class="la-lens__view-more-text">View Details</span>' +
          '<svg class="la-details-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
            '<polyline points="6 9 12 15 18 9"/>' +
          '</svg>';
        featuresWrap.insertAdjacentElement('afterend', btn);
      }

      // Mirror the FULL feature list into the bottom-sheet drawer so it
      // shows every feature (not just the 2 visible on the card).
      // Idempotent — only injects once.
      var popup = card.querySelector('.la-lens__details-popup');
      if (popup && !popup.querySelector('.la-popup__features-list')) {
        var allFeatures = featuresWrap.querySelectorAll('.la-lens__feature-item');
        if (allFeatures.length > 0) {
          var listWrap = document.createElement('div');
          listWrap.className = 'la-popup__features-list';
          // Color lens cards render distinct icons per row (colored
          // circle, eye, sun, etc.) — clone them as-is so the popup
          // shows the same icons the card does. For non-color cards
          // the icons are already tick-marks, so this also works.
          allFeatures.forEach(function(item) {
            listWrap.appendChild(item.cloneNode(true));
          });
          var popupHeader = popup.querySelector('.la-popup__header');
          if (popupHeader && popupHeader.parentNode) {
            popupHeader.parentNode.insertBefore(listWrap, popupHeader.nextSibling);
          }
        }
      }

      // Inject a primary CTA at the bottom of the bottom-sheet drawer
      // so the user can advance from inside the popup. Mirrors the
      // global sticky-footer CTA: zero-power -> "Add to Cart", everything
      // else -> "Continue to Power Details". Idempotent — only injects
      // once per popup.
      if (popup && !popup.querySelector('.la-popup__cta')) {
        var popupContent = popup.querySelector('.la-popup__content');
        if (popupContent) {
          var isZeroPower = drawer.classList.contains('is-zero-power');
          var ctaLabel = isZeroPower ? 'Add to Cart' : 'Continue to Power Details';
          var popupCta = document.createElement('button');
          popupCta.type = 'button';
          popupCta.className = 'la-popup__cta';
          popupCta.innerHTML =
            '<span>' + ctaLabel + '</span>' +
            '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
              '<path d="M5 12h14M13 5l7 7-7 7"/>' +
            '</svg>';
          popupCta.addEventListener('click', function (ev) {
            ev.stopPropagation();
            // Close the popup + backdrop first.
            popup.hidden = true;
            document.querySelectorAll('.la-popup-backdrop').forEach(function (b) { b.remove(); });
            var arrow = card.querySelector('.la-lens__view-more .la-details-arrow');
            if (arrow) arrow.style.transform = 'rotate(0deg)';
            // Dispatch a confirmed click on the parent lens card so the
            // main script's lens-card handler runs (selects the card,
            // then advances to step 3 / adds-to-cart for zero-power).
            var confirmedEvt = new MouseEvent('click', { bubbles: true, cancelable: true, view: window });
            confirmedEvt.__laConfirmed = true;
            card.dispatchEvent(confirmedEvt);
          });
          popupContent.appendChild(popupCta);
        }
      }
    }

    // Function to attach event listeners to lens cards
    function attachLensCardListeners() {
      drawer.querySelectorAll('.js-lens-card').forEach(card => {
        injectViewMoreButton(card);
        card.addEventListener('click', async (e) => {
          // Don't trigger if clicking the details button or anything inside the details popup (close button, backdrop, etc.)
          if (e.target.closest('.js-details-trigger') ||
              e.target.closest('.la-popup__close') ||
              e.target.closest('.la-lens__details-popup')) {
            return;
          }

          drawer.querySelectorAll('.la-lens').forEach(l => l.classList.remove('is-selected'));
          card.classList.add('is-selected');
          
          const handle = card.dataset.productHandle;
          // For zero power with card data from collection, skip product fetch (faster add-to-cart)
          let product = null;
          if (!(selectedLensType === 'zero-power' && card.dataset.variantId)) {
            product = await (await fetch(`/products/${handle}.js`)).json();
          }
          
          // Check if this is a zero power lens - skip step 3 and add directly to cart (quantity 1 only)
          if (selectedLensType === 'zero-power') {
            if (zeroPowerAddInProgress) return; // Prevent multiple adds when user clicks card repeatedly
            zeroPowerAddInProgress = true;
            const lensVariantId = card.dataset.variantId || (product && product.variants && product.variants[0] ? product.variants[0].id : null);
            const lensName = card.dataset.productTitle || (product && product.title) || card.querySelector('.la-lens__title')?.textContent?.trim() || '';
            
            if (!lensVariantId) {
              zeroPowerAddInProgress = false;
              alert('Cannot find lens variant. Please try again.');
              return;
            }
            
            // Get main product (frame) variant ID (set from page on drawer open for speed)
            let mainProductVariantId = window.mainProductVariantId;
            if (!mainProductVariantId) {
              const variantSelector = document.querySelector('select[name="id"], input[name="id"]');
              if (variantSelector) {
                mainProductVariantId = variantSelector.value;
              } else {
                const matches = window.location.pathname.match(/\/products\/([^\/\?#]+)/);
                if (matches && matches[1]) {
                  const productData = await fetch(`/products/${matches[1]}.js`).then(res => res.json());
                  if (productData && productData.variants && productData.variants[0]) {
                    mainProductVariantId = productData.variants[0].id;
                  }
                }
              }
            }
            
            if (!mainProductVariantId) {
              zeroPowerAddInProgress = false;
              alert('Cannot find frame product ID. Please try again.');
              return;
            }
            
            // Get lens and power type info
            const selectedPowerCard = drawer.querySelector('.la-power-card.is-selected');
            const powerType = selectedPowerCard ? selectedPowerCard.querySelector('.la-power-title').textContent.trim() : 'Zero Power';
            
            // Determine lens category for zero power based on selected filter or product
            let lensCategory = 'Colour Lenses'; // Default
            const activeFilter = drawer.querySelector('.la-filter-tab.is-active');
            if (activeFilter) {
              const filterText = activeFilter.textContent.trim().toLowerCase();
              if (filterText.includes('photochromic')) {
                lensCategory = 'Photochromic Lenses';
              } else if (filterText.includes('colour') || filterText.includes('color')) {
                lensCategory = 'Colour Lenses';
              } else if (filterText.includes('blue') || filterText.includes('block')) {
                lensCategory = 'Blue Block Lenses';
              }
            }
            
            // Get main product SKU and title (use cache from drawer open when available)
            let mainProductSku = window._mainProductSkuCache || '';
            let mainProductTitle = window._mainProductTitleCache || '';
            try {
              if ((!mainProductSku || !mainProductTitle) && window.product) {
                if (!mainProductSku && window.product.selected_or_first_available_variant) {
                  mainProductSku = window.product.selected_or_first_available_variant.sku || '';
                }
                if (!mainProductTitle) mainProductTitle = window.product.title || '';
              }
              if (!mainProductSku) {
                const variantSelector = document.querySelector('select[name="id"], input[name="id"]');
                if (variantSelector && variantSelector.value && window.product) {
                  const variant = window.product.variants.find(v => v.id == variantSelector.value);
                  if (variant) {
                    mainProductSku = variant.sku || '';
                  }
                }
              }
              if (!mainProductSku || !mainProductTitle) {
                const matches = window.location.pathname.match(/\/products\/([^\/\?#]+)/);
                if (matches && matches[1]) {
                  const productData = await fetch(`/products/${matches[1]}.js`).then(res => res.json());
                  if (productData) {
                    if (!mainProductSku && productData.variants) {
                      const currentVariant = productData.variants.find(v => v.id == mainProductVariantId) || productData.variants[0];
                      if (currentVariant) {
                        mainProductSku = currentVariant.sku || '';
                      }
                    }
                    if (!mainProductTitle) mainProductTitle = productData.title || '';
                  }
                }
              }
              if (!mainProductSku) {
                mainProductSku = `VAR-${mainProductVariantId}`;
              }
              if (!mainProductTitle) {
                mainProductTitle = mainProductSku;
              }
            } catch (err) {
              console.error('Error getting main product SKU/title:', err);
              mainProductSku = mainProductSku || `VAR-${mainProductVariantId}`;
              mainProductTitle = mainProductTitle || mainProductSku;
            }
            
            // Create items array for cart
            const goeyePairIdZero = goeyeLensFramePairId();
            let items = [
              {
                id: lensVariantId,
                quantity: 1,
                properties: {
                  'Lens Type': 'Zero power',
                  'Power Type': lensCategory,
                  'Lens Brand': lensName,
                  'Prescription': 'Zero Power - No Prescription Needed',
                  'Frame SKU': mainProductSku,
                  'Associated Frame': mainProductTitle,
                  '_bundle_group_id': goeyePairIdZero
                }
              },
              {
                id: mainProductVariantId,
                quantity: 1,
                properties: {
                  'Frame': 'With Custom Lenses',
                  '_bundle': 'lens-frame',
                  '_bundle_group_id': goeyePairIdZero
                }
              }
            ];
            
            // Add fitting fee for LensAdvizor products
            if (window.fittingFeeHelper) {
              items = await window.fittingFeeHelper.addToItems(items);
            }
            
            console.log('Adding zero power lens to cart:', items);

            try {
              // Add items to cart (always quantity 1)
              showLensLoader();
              const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ items })
              });
              
              if (!response.ok) {
                let errorMessage = 'Failed to add items to cart';
                try {
                  const errorData = await response.json();
                  errorMessage = errorData.description || errorMessage;
                } catch (parseError) {
                  console.error('Error parsing response:', parseError);
                }
                throw new Error(errorMessage);
              }
              
              const result = await response.json();
              console.log('Cart success:', result);
              
              // Close the lens selector drawer and open cart
              close();
              openCartDrawerAfterAdd();
              return;
            } catch (err) {
              console.error('Error adding to cart:', err);
              alert(err.message || 'Failed to add items to cart. Please try again.');
              return;
            } finally {
              zeroPowerAddInProgress = false;
            }
          }
          
          // For non-zero-power lenses, proceed to step 3
          const wrap = drawer.querySelector('.la-content--step-3');
          const list = wrap.querySelector('.la-list--powers');
          const powerSelectionOptions = wrap.querySelector('.la-power-selection-options');
          
          // Hide variant list initially, show power selection options
          if (list) list.style.display = 'none';
          if (powerSelectionOptions) powerSelectionOptions.style.display = 'block';
          
          if (list) list.innerHTML = '';
          selectedLensVariantId = null;
          window.selectedLensVariantId = null;
  
          // Go to step 3
          goToStep(3);
          
          // Store product data and handle for later use
          window.selectedLensProduct = product;
          window.selectedLensHandle = handle;
          
          // Ensure power selection options are visible
          if (powerSelectionOptions) {
            powerSelectionOptions.style.display = 'block';
          }
          
          // Create power variant options (will be shown when "I know my power" is selected)
          product.variants.forEach(v => {
            const el = document.createElement('div');
            el.className = 'la-power-item';
            el.dataset.variantId = v.id;
            
            // Custom pricing logic for specific variants
            let displayPrice = (v.price/100).toFixed(2);
            let isFreeLens = false;
            
            // Check if this is the lens02 SKU that should show ₹399 but is actually free
            if (v.sku && v.sku.toLowerCase() === 'lens02') {
              displayPrice = '399.00';
              isFreeLens = true;
              console.log('🔍 LENS SELECTOR: Setting custom display price for SKU lens02:', v.title, 'Display: ₹399, Original: ₹' + (v.price/100), 'Free: true');
            }
            
            // Check if this is the lens3 SKU that should show ₹499 but is actually free
            if (v.sku && v.sku.toLowerCase() === 'lens3') {
              displayPrice = '499.00';
              isFreeLens = true;
              console.log('🔍 LENS SELECTOR: Setting custom display price for SKU lens3:', v.title, 'Display: ₹499, Original: ₹' + (v.price/100), 'Free: true');
            }
            
            // Check if this is the lens4 SKU that should show ₹699 but is actually free
            if (v.sku && v.sku.toLowerCase() === 'lens4') {
              displayPrice = '699.00';
              isFreeLens = true;
              console.log('🔍 LENS SELECTOR: Setting custom display price for SKU lens4:', v.title, 'Display: ₹699, Original: ₹' + (v.price/100), 'Free: true');
            }
            
            // Check if this is the lens5 SKU that should show ₹399 but is actually free
            if (v.sku && v.sku.toLowerCase() === 'lens5') {
              displayPrice = '399.00';
              isFreeLens = true;
              console.log('🔍 LENS SELECTOR: Setting custom display price for SKU lens5:', v.title, 'Display: ₹399, Original: ₹' + (v.price/100), 'Free: true');
            }
            
            // Check if this is the lens6 SKU that should show ₹599 but is actually free
            if (v.sku && v.sku.toLowerCase() === 'lens6') {
              displayPrice = '599.00';
              isFreeLens = true;
              console.log('🔍 LENS SELECTOR: Setting custom display price for SKU lens6:', v.title, 'Display: ₹599, Original: ₹' + (v.price/100), 'Free: true');
            }
            
            console.log('🔍 LENS SELECTOR: Variant details:', {
              title: v.title,
              sku: v.sku,
              originalPrice: (v.price/100),
              displayPrice: displayPrice,
              isFreeLens: isFreeLens
            });
            
            // Generate price display with free tag if applicable
            let priceHtml = '';
            if (isFreeLens) {
              priceHtml = `
                <div class="la-power-item__price-wrapper">
                  <div class="la-power-item__price-container">
                    <span class="la-power-item__original-price">₹${displayPrice}</span>
                    <span class="la-power-item__current-price">FREE</span>
                  </div>
                  <div class="la-power-item__savings-badge">Save ₹${displayPrice}</div>
                </div>
              `;
            } else {
              priceHtml = `<div class="la-power-item__price">₹${displayPrice}</div>`;
            }
            
            el.innerHTML = `
              <div class="la-power-item__content">
                <div class="la-power-item__title">${v.title}</div>
                ${priceHtml}
              </div>
              <div class="la-power-item__select">
                <div class="la-select-circle"></div>
              </div>
            `;
            
            // Add click handler
            el.addEventListener('click', () => {
              // Remove selected class from all items
              list.querySelectorAll('.la-power-item').forEach(item => {
                item.classList.remove('is-selected');
              });
              
              // Add selected class to clicked item
              el.classList.add('is-selected');
              
              // Update selected variant (both local and global)
              selectedLensVariantId = v.id;
              window.selectedLensVariantId = v.id;
              
              // Enable/disable add to cart button based on validation
              addToCartBtn.disabled = !validateStep(3);
            });
            
            list.appendChild(el);
          });
          
          // Set up power selection handlers (will remove old ones and attach fresh ones)
          // Use setTimeout to ensure DOM is ready and data is stored
          setTimeout(() => {
            setupPowerSelectionHandlers();
          }, 0);
  
          // Manual prescription form
          const manualTrigger = drawer.querySelector('[data-prescription-method="manual"]');
          const manualForm = drawer.querySelector('.la-manual-prescription');
          const prescriptionList = drawer.querySelector('.la-list--prescription');
          const backButton = drawer.querySelector('.la-back-button');
  
          if (manualTrigger) {
          manualTrigger.addEventListener('click', () => {
              if (prescriptionList) prescriptionList.style.display = 'none';
              if (manualForm) manualForm.style.display = 'block';
              updatePowerSelectorRange();
              if (addToCartBtn) addToCartBtn.style.display = 'none';
              
              // Enable the Save Prescription button
              const savePrescriptionBtn = drawer.querySelector('.la-save-prescription');
              if (savePrescriptionBtn) {
                savePrescriptionBtn.disabled = false;
                savePrescriptionBtn.style.opacity = '1';
              }
              
              // Ensure Save Prescription handler is attached when manual form is shown
              setupSavePrescriptionHandler();
            });
          }
  
          if (backButton) {
          backButton.addEventListener('click', () => {
              // Check if we came from power selection options or prescription list
              const powerSelectionOptions = wrap.querySelector('.la-power-selection-options');
              const isFromPowerSelection = powerSelectionOptions && powerSelectionOptions.style.display !== 'none';
              
              if (isFromPowerSelection || !prescriptionList) {
                // Go back to power selection options
                if (powerSelectionOptions) {
                  powerSelectionOptions.style.display = 'block';
                }
                if (manualForm) {
                  manualForm.style.display = 'none';
                }
                if (list) {
                  list.style.display = 'none';
                }
              } else {
                // Go back to prescription list (old flow)
                if (prescriptionList) {
            prescriptionList.style.display = 'flex';
                }
                if (manualForm) {
            manualForm.style.display = 'none';
                }
                if (addToCartBtn) {
            addToCartBtn.style.display = 'flex';
                }
            window.prescriptionData = null;
                if (addToCartBtn) {
                  addToCartBtn.disabled = !validateStep(3);
                }
              }
          });
          }
  
          // Set up Save Prescription handler (will remove old one and attach fresh one)
          setupSavePrescriptionHandler();
          
          // OLD HANDLER CODE REMOVED - Now handled by setupSavePrescriptionHandler() function
          // The handler is now set up when drawer opens and when manual form is shown
  
          // Email Later option
          const emailMethodBtn = drawer.querySelector('[data-prescription-method="email"]');
          if (emailMethodBtn) {
            emailMethodBtn.addEventListener('click', function() {
              drawer.querySelectorAll('.la-prescription-option').forEach(opt => opt.classList.remove('is-selected'));
              this.classList.add('is-selected');
              prescriptionData = null;
              window.prescriptionData = null;
              addToCartBtn.disabled = !selectedLensVariantId;
            });
          }
  
          goToStep(3);
        });
  
        // Details popup handling
        const detailsTrigger = card.querySelector('.js-details-trigger');
        if (detailsTrigger) {
          detailsTrigger.addEventListener('click', (e) => {
            e.stopPropagation();
            const currentPopup = card.querySelector('.la-lens__details-popup');
            const isCurrentlyHidden = currentPopup.hidden;
            
            // Close any other open popups first
            drawer.querySelectorAll('.la-lens__details-popup').forEach(popup => {
              popup.hidden = true;
            });
            
            // Remove any existing backdrops
            document.querySelectorAll('.la-popup-backdrop').forEach(backdrop => {
              backdrop.remove();
            });
            
            if (!isCurrentlyHidden) {
              currentPopup.hidden = true;
              detailsTrigger.querySelector('.la-details-arrow').style.transform = 'rotate(0deg)';
            } else {
              // Create and add backdrop
              const backdrop = document.createElement('div');
              backdrop.className = 'la-popup-backdrop';
              document.body.appendChild(backdrop);
              
              // Show popup
              currentPopup.hidden = false;
              detailsTrigger.querySelector('.la-details-arrow').style.transform = 'rotate(180deg)';
              
              // Close popup when clicking backdrop
              backdrop.addEventListener('click', (ev) => {
                ev.stopPropagation();
                currentPopup.hidden = true;
                backdrop.remove();
                detailsTrigger.querySelector('.la-details-arrow').style.transform = 'rotate(0deg)';
              });

              // Close popup when clicking close button
              const closeBtn = currentPopup.querySelector('.la-popup__close');
              if (closeBtn) {
                closeBtn.addEventListener('click', (ev) => {
                  ev.stopPropagation();
                  currentPopup.hidden = true;
                  backdrop.remove();
                  detailsTrigger.querySelector('.la-details-arrow').style.transform = 'rotate(0deg)';
                });
              }

              // Swallow any stray clicks on the popup container itself so they don't bubble to the lens card
              currentPopup.addEventListener('click', (ev) => {
                ev.stopPropagation();
              });
            }
          });
        }
      });
    }
  
    // File upload handling
    const fileInput = drawer.querySelector('#upload-prescription');
    const uploadLabel = drawer.querySelector('.la-upload-label');
    const uploadInfo = drawer.querySelector('.la-upload-info');
    const filename = drawer.querySelector('.la-filename');
    const removeFileBtn = drawer.querySelector('.la-remove-file');
    const uploadProgress = drawer.querySelector('.la-upload-progress');
    const addToCartBtn = drawer.querySelector('.la-add-to-cart');
  
    let prescriptionData = null;
    let selectedLensVariantId = null;
  
    function resetFileUpload() {
      fileInput.value = '';
      uploadLabel.style.display = 'block';
      uploadInfo.style.display = 'none';
      uploadProgress.style.display = 'none';
      prescriptionData = null;
      addToCartBtn.disabled = !validateStep(4);
    }
  
    fileInput.addEventListener('change', async (e) => {
      const file = e.target.files[0];
      if (!file) return;
  
      try {
        const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
        if (!validTypes.includes(file.type)) {
          throw new Error('Please upload a PDF or image file (JPG/PNG)');
        }
        if (file.size > 5 * 1024 * 1024) {
          throw new Error('File size must be less than 5MB');
        }
  
        uploadProgress.style.display = 'block';
        
        // Store the file directly
        prescriptionData = file;
        
        // Show upload complete after a brief delay
        setTimeout(() => {
          uploadProgress.style.display = 'none';
          uploadLabel.style.display = 'none';
          uploadInfo.style.display = 'flex';
          filename.textContent = file.name;
          addToCartBtn.disabled = !validateStep(4);
        }, 1000);
  
      } catch (error) {
        alert(error.message);
        resetFileUpload();
      }
    });
  
    removeFileBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      resetFileUpload();
    });
  
        // Add to Cart
    addToCartBtn.addEventListener('click', async () => {
      if (!validateStep(3)) {
        showError(3);
        return;
      }

      try {
        // Show loading state briefly
        addToCartBtn.disabled = true;
        addToCartBtn.textContent = 'Adding…';
        
        // Close the drawer immediately on desktop to prevent interaction blocking
        if (window.innerWidth >= 750) {
          if (typeof window.closeDesktopLensDrawer === 'function') {
            window.closeDesktopLensDrawer();
          } else {
            close();
          }
        } else {
          // Mobile: smooth delay
          setTimeout(() => {
            close();
          }, 150);
        }
        console.log('🚪 Lens selector drawer closed after add to cart click');
        
        console.log('Adding lens and frame to cart...');
        console.log('Selected lens variant ID:', selectedLensVariantId);
        console.log('Main product variant ID:', window.mainProductVariantId);
        
        // Log pricing information for debugging
        const selectedVariantElement = drawer.querySelector('.la-power-item.is-selected');
        if (selectedVariantElement) {
          // Try to get price from different possible elements due to new FREE tag structure
          let displayPrice = '';
          const regularPriceElement = selectedVariantElement.querySelector('.la-power-item__price');
          const originalPriceElement = selectedVariantElement.querySelector('.la-power-item__original-price');
          const currentPriceElement = selectedVariantElement.querySelector('.la-power-item__current-price');
          const savingsBadgeElement = selectedVariantElement.querySelector('.la-power-item__savings-badge');
          
          if (currentPriceElement && currentPriceElement.textContent === 'FREE') {
            // This is a free lens item
            displayPrice = originalPriceElement ? originalPriceElement.textContent + ' (FREE)' : 'FREE';
          } else if (regularPriceElement) {
            // This is a regular priced item
            displayPrice = regularPriceElement.textContent;
          } else {
            displayPrice = 'Price not found';
          }
          
          console.log('🔍 LENS SELECTOR: Display price shown to user:', displayPrice);
          console.log('🔍 LENS SELECTOR: Cart will use original Shopify price, free lens logic will apply if SKU is lens02/lens01');
        }
  
        // First, check if we have valid variant IDs
        if (!selectedLensVariantId) {
          throw new Error('Please select a lens option');
        }
        
        if (!window.mainProductVariantId) {
          // Try to get the main product variant ID again
          const variantSelector = document.querySelector('select[name="id"], input[name="id"]');
          if (variantSelector) {
            window.mainProductVariantId = variantSelector.value;
          } else {
            // Use URL to extract product ID if needed
            const matches = window.location.pathname.match(/\/products\/([^\/\?#]+)/);
            if (matches && matches[1]) {
              try {
                const productData = await fetch(`/products/${matches[1]}.js`).then(res => res.json());
                if (productData && productData.variants && productData.variants[0]) {
                  window.mainProductVariantId = productData.variants[0].id;
                }
              } catch (err) {
                console.error('Error fetching product data:', err);
              }
            }
          }
          
          if (!window.mainProductVariantId) {
            throw new Error('Cannot find frame product ID');
          }
        }
  
        // Get selected power type and lens
        const selectedPowerCard = drawer.querySelector('.la-power-card.is-selected');
        const selectedLensEl = drawer.querySelector('.la-lens.is-selected');
  
        // Structure lens details for both items
        const powerType = selectedPowerCard ? selectedPowerCard.querySelector('.la-power-title').textContent.trim() : '';
        const lensName = selectedLensEl ? selectedLensEl.querySelector('.la-lens__title').textContent.trim() : '';
        const lensType = selectedLensType || powerType || '';
  
        // Get main product SKU and title for lens association
        let mainProductSku = '';
        let mainProductTitle = '';
        try {
          // Method 1: Try to get SKU and title from current product data
          if (window.product && window.product.selected_or_first_available_variant) {
            mainProductSku = window.product.selected_or_first_available_variant.sku || '';
            mainProductTitle = window.product.title || '';
          }
          
          // Method 2: Try to get from variant selector if not found
          if (!mainProductSku) {
            const variantSelector = document.querySelector('select[name="id"], input[name="id"]');
            if (variantSelector && variantSelector.value && window.product) {
              const variant = window.product.variants.find(v => v.id == variantSelector.value);
              if (variant) {
                mainProductSku = variant.sku || '';
              }
            }
          }
          
          // Method 3: Fetch from API if still not found
          if (!mainProductSku || !mainProductTitle) {
            const matches = window.location.pathname.match(/\/products\/([^\/\?#]+)/);
            if (matches && matches[1]) {
              try {
                const productData = await fetch(`/products/${matches[1]}.js`).then(res => res.json());
                if (productData) {
                  if (!mainProductSku && productData.variants) {
                    const currentVariant = productData.variants.find(v => v.id == window.mainProductVariantId) || productData.variants[0];
                    if (currentVariant) {
                      mainProductSku = currentVariant.sku || '';
                    }
                  }
                  if (!mainProductTitle) mainProductTitle = productData.title || '';
                }
              } catch (err) {
                console.error('Error fetching product SKU/title:', err);
              }
            }
          }
          
          // Fallback: use variant ID if no SKU found
          if (!mainProductSku) {
            mainProductSku = `VAR-${window.mainProductVariantId}`;
          }
          if (!mainProductTitle) {
            mainProductTitle = mainProductSku;
          }
          
        } catch (err) {
          console.error('Error getting main product SKU/title:', err);
          mainProductSku = mainProductSku || `VAR-${window.mainProductVariantId}`;
          mainProductTitle = mainProductTitle || mainProductSku;
        }
  
        console.log('Lens details:', { lensType, powerType, lensName, mainProductSku, mainProductTitle });

        // Create items array for cart
        // Note: Cart uses original Shopify prices - the free lens logic in cart drawer
        // will automatically make lens02/lens01 SKUs free regardless of original price
        const goeyePairIdMain = goeyeLensFramePairId();
        let items = [
          {
            id: selectedLensVariantId,
            quantity: 1,
            properties: {
              'Lens Type': powerType || 'With Power',
              'Power Type': powerType || 'With Power',
              'Lens Brand': lensName,
              'Frame SKU': mainProductSku,
              'Associated Frame': mainProductTitle,
              '_bundle_group_id': goeyePairIdMain
            }
          },
          {
            id: window.mainProductVariantId,
            quantity: 1,
            properties: {
              'Frame': 'With Custom Lenses',
              '_bundle': 'lens-frame',
              '_bundle_group_id': goeyePairIdMain
            }
          }
        ];
        
        // Add prescription information
        if (prescriptionData) {
          items[0].properties['Prescription'] = 'File Upload';
          // Check if prescriptionData is a URL or a filename
          const isUrl = prescriptionData.startsWith('http://') || prescriptionData.startsWith('https://');
          if (isUrl) {
            items[0].properties['Prescription Image (Click to View)'] = prescriptionData;
          } else {
            items[0].properties['Prescription File'] = prescriptionData;
          }
        } else if (window.prescriptionData) {
          const rightEye = window.prescriptionData.right_eye;
          const leftEye = window.prescriptionData.left_eye;
          
          items[0].properties['Prescription'] = 'Manual Entry';
          items[0].properties['Right Eye (OD)'] = 
            `SPH: ${rightEye.sph || "0.00"}, CYL: ${rightEye.cyl || "0.00"}, AXIS: ${rightEye.axis || "N/A"}`;
          items[0].properties['Left Eye (OS)'] = 
            `SPH: ${leftEye.sph || "0.00"}, CYL: ${leftEye.cyl || "0.00"}, AXIS: ${leftEye.axis || "N/A"}`;
        } else {
          items[0].properties['Prescription'] = 'Email Later';
        }

        // Add fitting fee for LensAdvizor products
        let fittingFeeVariantId = null;
        if (window.fittingFeeHelper) {
          items = await window.fittingFeeHelper.addToItems(items);
          // Check if fitting fee was added (for FormData path)
          const fittingFeeItem = items.find(item => item.properties && item.properties._fitting_fee === 'true');
          if (fittingFeeItem) {
            fittingFeeVariantId = fittingFeeItem.id;
          }
        }

        let response;
        
        // If we have a file upload, use Shopify's native form-based upload
        if (prescriptionData && window.prescriptionFileObject) {
          items[0].properties['Prescription'] = 'File Upload';
          
          console.log('Using Shopify native file upload');
          
          // Create hidden form for native file upload
          const hiddenForm = document.createElement('form');
          hiddenForm.method = 'POST';
          hiddenForm.action = '/cart/add';
          hiddenForm.enctype = 'multipart/form-data';
          hiddenForm.style.display = 'none';
          
          const addHiddenInput = (name, value) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            hiddenForm.appendChild(input);
          };
          
          // Add all items via hidden inputs
          items.forEach((item, index) => {
            addHiddenInput(`items[${index}][id]`, item.id);
            addHiddenInput(`items[${index}][quantity]`, item.quantity);
            if (item.properties) {
              Object.entries(item.properties).forEach(([key, value]) => {
                addHiddenInput(`items[${index}][properties][${key}]`, value);
              });
            }
          });
          
          // Add prescription file input
          const fileInput = document.createElement('input');
          fileInput.type = 'file';
          fileInput.name = 'items[0][properties][Prescription File]';
          fileInput.style.display = 'none';
          hiddenForm.appendChild(fileInput);
          
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(window.prescriptionFileObject);
          fileInput.files = dataTransfer.files;
          
          document.body.appendChild(hiddenForm);
          
          // Submit via fetch to stay on page
          const formData = new FormData(hiddenForm);
          showLensLoader();
          response = await fetch('/cart/add', {
            method: 'POST',
            body: formData
          });
          
          document.body.removeChild(hiddenForm);
          
        } else {
          // No file upload, use JSON
          console.log('Sending JSON request');
          showLensLoader();
          response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ items })
          });
        }
  
        console.log('Cart response status:', response.status);
        
        // Handle response
        if (!response.ok) {
          let errorMessage = 'Failed to add items to cart';
          try {
            const errorText = await response.text();
            console.error('Cart error:', errorText);
          } catch (parseError) {
            console.error('Error parsing response:', parseError);
          }
          throw new Error(errorMessage);
        }
        
        console.log('Cart success');

        // Cart operations completed - open cart drawer
        console.log('🛒 Cart operations completed, opening cart drawer');
        openCartDrawerAfterAdd();
      } catch (err) {
        console.error('Cart error:', err);
        alert(err.message || 'Failed to add items to cart. Please try again.');
        
        // Re-open lens selector drawer if there was an error
        setTimeout(() => {
          open();
          goToStep(3);
          console.log('🚪 Lens selector drawer re-opened due to error');
        }, 300);
      } finally {
        hideLensLoader(true);
        addToCartBtn.disabled = false;
        addToCartBtn.textContent = 'Save and Continue';
      }
    });
  
    // Initial attachment of lens card listeners (for static cards)
    attachLensCardListeners();
  });
  
  // Add this new code before the closing script tag
  document.addEventListener('cart:refresh', function(event) {
    // Find all cart items
    const cartItems = document.querySelectorAll('.cart__item');
    
    cartItems.forEach(item => {
      const properties = item.querySelector('.cart__item-properties');
      if (!properties) return;
  
      // Get all properties and sort them
      const props = Array.from(properties.children);
      props.sort((a, b) => {
        const aText = a.textContent.trim();
        const bText = b.textContent.trim();
        // Extract numbers from property names for sorting
        const aNum = parseInt(aText.match(/^\d+\./) || [0]);
        const bNum = parseInt(bText.match(/^\d+\./) || [0]);
        return aNum - bNum;
      });
  
      // Clear and re-append properties in order
      properties.innerHTML = '';
      props.forEach(prop => {
        if (!prop.textContent.includes('_prescription')) {
          const propDiv = document.createElement('div');
          propDiv.className = 'cart__item-property';
          propDiv.style.fontSize = '12px';
          propDiv.style.color = '#666';
          propDiv.style.marginTop = '4px';
          propDiv.textContent = prop.textContent;
          properties.appendChild(propDiv);
        }
      });
  
      // Style the properties container
      properties.style.marginTop = '8px';
      properties.style.paddingTop = '8px';
      properties.style.borderTop = '1px solid #eee';
    });
  });
  
  // Add styles for cart properties
  const styles = `
    .cart__item-properties {
      font-size: 12px;
      color: #666;
      margin-top: 8px;
    }
    .cart__item-property {
      margin-bottom: 4px;
      line-height: 1.4;
    }
    .cart__item-property:last-child {
      margin-bottom: 0;
    }
  `;
  
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
